import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";

/**
 * Hero3D — playable cinematic mechanical slot device.
 *
 * Scroll-driven phases (`progressRef.current`, 0..1):
 *   0.00 - 0.36   Idle slow rotation
 *   0.36 - 0.62   Mechanical explosion (parts radiate out)
 *   0.62 - 0.78   Reassembly back into the original device
 *
 * Chapter-driven (`chapterRef.current` 0..4): camera smoothly travels between
 * pre-set "shots" so the device sits prominently for each section.
 *
 * Game (foreground):
 *   - SPIN button (or lever pull, or reel click) starts a spin if credits >= bet.
 *   - Each reel decelerates onto a discrete symbol; a win evaluator pays out.
 *   - Win → glow + sparkle particle burst. Jackpot → full strobe + beacon spin.
 *   - BET button cycles bet value. CREDIT button adds demo credits.
 *
 * Lever:
 *   - Anchored properly: a chrome housing protrudes from the right side of
 *     the body; the arm pivots from inside the housing at a fixed pivot
 *     group, so dragging it visually swings down and snaps back.
 *
 * Interaction:
 *   - Mouse parallax + drag-to-rotate elsewhere on the device.
 *   - Hover highlights interactive parts; cursor + canvas pointer-events
 *     auto-toggle so page links remain clickable.
 */

// ========================================================================
// Constants & helpers
// ========================================================================

const smoothstep = (a, b, x) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};
const lerp = (a, b, t) => a + (b - a) * t;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeOutQuint = (t) => 1 - Math.pow(1 - t, 5);
const FONT_STACK =
  "'Inter', 'SF Pro Display', 'Segoe UI', 'Helvetica', sans-serif";

// Reel symbols. SAME set on every reel so a fair comparison is possible.
// '7' is the jackpot symbol (highest payout).
const SYMBOLS = [
  { glyph: "7", color: "#ff5252", isJackpot: true },
  { glyph: "💎", color: "#7ec8ff" },
  { glyph: "⭐", color: "#ffd454" },
  { glyph: "🍀", color: "#65e0a4" },
  { glyph: "🍒", color: "#ff7a9e" },
  { glyph: "🔔", color: "#f0c069" },
];
const N_SYMBOLS = SYMBOLS.length;

const BET_OPTIONS = [10, 25, 50, 100];

// Per-chapter palettes — JS-side smoothed lerp drives shader uniforms +
// gas-cloud sprite tints + dust color. base/mid/acc/warm matches the page
// chapter accents (cyan → mint → amber → rose → violet).
const CHAPTER_PALETTES = [
  { base: 0x040712, mid: 0x0e1a3a, acc: 0x82b1f7, warm: 0xffb86b }, // 0 INTRO   - cyan/navy
  { base: 0x041413, mid: 0x0c2a26, acc: 0x65e0a4, warm: 0xb8ffd8 }, // 1 PROFILE - mint
  { base: 0x14110a, mid: 0x2a210e, acc: 0xffd454, warm: 0xff944d }, // 2 SKILLS  - amber
  { base: 0x140a14, mid: 0x2a1424, acc: 0xff9bb7, warm: 0xc78cff }, // 3 SERVICES- rose
  { base: 0x0e0a18, mid: 0x1a1130, acc: 0xc4a8ff, warm: 0x8b6cff }, // 4 CONNECT - violet
];

// ========================================================================
// Texture / canvas helpers
// ========================================================================

function makeBrandTexture() {
  const c = document.createElement("canvas");
  c.width = 1024;
  c.height = 256;
  const ctx = c.getContext("2d");

  // dark brushed-metal background
  const grad = ctx.createLinearGradient(0, 0, 0, c.height);
  grad.addColorStop(0, "#16181d");
  grad.addColorStop(0.5, "#0c0d10");
  grad.addColorStop(1, "#16181d");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, c.width, c.height);

  ctx.strokeStyle = "rgba(255,255,255,0.04)";
  ctx.lineWidth = 1;
  for (let x = 0; x < c.width; x += 32) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, c.height);
    ctx.stroke();
  }

  ctx.fillStyle = "rgba(160, 200, 255, 0.18)";
  ctx.fillRect(0, c.height - 8, c.width, 3);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

function makeMarqueeTexture() {
  const c = document.createElement("canvas");
  c.width = 2048;
  c.height = 512;
  const ctx = c.getContext("2d");

  // Backlit warm glow gradient
  const grad = ctx.createRadialGradient(
    c.width / 2,
    c.height / 2,
    50,
    c.width / 2,
    c.height / 2,
    900,
  );
  grad.addColorStop(0, "#1f2330");
  grad.addColorStop(0.5, "#0c0e14");
  grad.addColorStop(1, "#06080c");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, c.width, c.height);

  // Diagonal pinstripes
  ctx.strokeStyle = "rgba(180, 215, 255, 0.04)";
  ctx.lineWidth = 1;
  for (let i = -c.height; i < c.width + c.height; i += 16) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i + c.height, c.height);
    ctx.stroke();
  }

  // Bezel inner border
  ctx.strokeStyle = "rgba(180, 215, 255, 0.32)";
  ctx.lineWidth = 6;
  ctx.strokeRect(48, 48, c.width - 96, c.height - 96);
  ctx.strokeStyle = "rgba(180, 215, 255, 0.12)";
  ctx.lineWidth = 2;
  ctx.strokeRect(58, 58, c.width - 116, c.height - 116);

  // Brand
  ctx.shadowColor = "rgba(130, 177, 247, 0.85)";
  ctx.shadowBlur = 32;
  ctx.font = `bold 240px ${FONT_STACK}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  const cx = c.width / 2;
  const cy = c.height / 2 + 16;
  ctx.fillStyle = "#f4f7ff";
  ctx.fillText("KEDI", cx - 200, cy);
  ctx.fillStyle = "#82b1f7";
  ctx.fillText("GX", cx + 240, cy);

  ctx.shadowBlur = 0;
  ctx.font = `600 56px ${FONT_STACK}`;
  ctx.fillStyle = "rgba(226, 241, 255, 0.5)";
  ctx.letterSpacing = "0.5em";
  ctx.fillText("· Yes, that's my online name ·", cx, c.height - 96);

  // Star points around brand
  ctx.fillStyle = "#82b1f7";
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const r = 380;
    const px = cx + Math.cos(a) * r;
    const py = cy + Math.sin(a) * r * 0.35;
    ctx.beginPath();
    ctx.arc(px, py, 4, 0, Math.PI * 2);
    ctx.fill();
  }

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/**
 * Programmable LCD display. Returns the texture and a `draw(state)` function
 * that re-renders based on game state and triggers `tex.needsUpdate`.
 */
function makeDisplay() {
  const c = document.createElement("canvas");
  c.width = 1024;
  c.height = 192;
  const ctx = c.getContext("2d");

  let lastSig = "";

  const draw = (state) => {
    const sig = JSON.stringify(state);
    if (sig === lastSig) return false;
    lastSig = sig;

    ctx.fillStyle = "#070a0e";
    ctx.fillRect(0, 0, c.width, c.height);

    // CRT scanlines
    for (let y = 0; y < c.height; y += 4) {
      ctx.fillStyle = "rgba(120, 200, 255, 0.05)";
      ctx.fillRect(0, y, c.width, 1);
    }
    // Subtle noise sparkle
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `rgba(150, 220, 255, ${Math.random() * 0.06})`;
      ctx.fillRect(
        Math.random() * c.width,
        Math.random() * c.height,
        Math.random() * 2,
        Math.random() * 2,
      );
    }

    const status = state.status || "IDLE";
    const credits = state.credits ?? 0;
    const bet = state.bet ?? 0;
    const lastWin = state.lastWin ?? 0;

    // Big status line
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    if (status === "JACKPOT") {
      ctx.shadowColor = "#ffd454";
      ctx.shadowBlur = 28;
      ctx.font = `bold 76px 'Courier New', monospace`;
      ctx.fillStyle = "#ffd454";
      ctx.fillText(`JACKPOT  +${lastWin}`, c.width / 2, 64);
    } else if (status === "WIN") {
      ctx.shadowColor = "#65e0a4";
      ctx.shadowBlur = 22;
      ctx.font = `bold 70px 'Courier New', monospace`;
      ctx.fillStyle = "#9ff5c8";
      ctx.fillText(`WIN  +${lastWin}`, c.width / 2, 64);
    } else if (status === "LOSE") {
      ctx.shadowColor = "#ff7a8e";
      ctx.shadowBlur = 18;
      ctx.font = `bold 64px 'Courier New', monospace`;
      ctx.fillStyle = "#ff8aa1";
      ctx.fillText(`TRY  AGAIN`, c.width / 2, 64);
    } else if (status === "SPINNING") {
      ctx.shadowColor = "#5aa8ff";
      ctx.shadowBlur = 22;
      ctx.font = `bold 64px 'Courier New', monospace`;
      ctx.fillStyle = "#a8d4ff";
      ctx.fillText(`SPINNING . . .`, c.width / 2, 64);
    } else if (status === "INSERT") {
      ctx.shadowColor = "#ffd454";
      ctx.shadowBlur = 18;
      ctx.font = `bold 60px 'Courier New', monospace`;
      ctx.fillStyle = "#ffd454";
      ctx.fillText(`INSERT  COIN`, c.width / 2, 64);
    } else {
      ctx.shadowColor = "#5aa8ff";
      ctx.shadowBlur = 18;
      ctx.font = `bold 60px 'Courier New', monospace`;
      ctx.fillStyle = "#a8d4ff";
      ctx.fillText("READY  TO  SPIN", c.width / 2, 64);
    }
    ctx.shadowBlur = 0;

    // Bottom HUD: CREDITS · BET · WIN
    ctx.font = `bold 36px 'Courier New', monospace`;
    const baseY = 142;
    const cellW = c.width / 3;

    const drawCell = (label, value, x, color) => {
      ctx.fillStyle = "rgba(150, 200, 255, 0.45)";
      ctx.font = `500 22px 'Courier New', monospace`;
      ctx.fillText(label, x, baseY - 20);
      ctx.shadowColor = color;
      ctx.shadowBlur = 10;
      ctx.fillStyle = color;
      ctx.font = `bold 38px 'Courier New', monospace`;
      ctx.fillText(value, x, baseY + 16);
      ctx.shadowBlur = 0;
    };

    drawCell("CREDITS", String(credits).padStart(4, "0"), cellW * 0.5, "#a8d4ff");
    drawCell("BET", String(bet).padStart(3, "0"), cellW * 1.5, "#ffd454");
    drawCell("LAST WIN", `+${lastWin}`.padStart(5, "0"), cellW * 2.5, "#9ff5c8");

    // Vertical separators
    ctx.strokeStyle = "rgba(120, 200, 255, 0.15)";
    ctx.lineWidth = 1;
    [cellW, cellW * 2].forEach((x) => {
      ctx.beginPath();
      ctx.moveTo(x, 96);
      ctx.lineTo(x, c.height - 16);
      ctx.stroke();
    });

    return true;
  };

  draw({ status: "IDLE", credits: 100, bet: 10, lastWin: 0 });

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return { tex, draw };
}

/**
 * Reel strip texture: each symbol is a cell. Texture wraps around the
 * cylinder; offset.x scrolls them past the visible face.
 */
function makeReelStripTexture() {
  const cellW = 256;
  const cellH = 256;
  const c = document.createElement("canvas");
  c.width = cellW * N_SYMBOLS;
  c.height = cellH;
  const ctx = c.getContext("2d");

  // Background
  ctx.fillStyle = "#0e1014";
  ctx.fillRect(0, 0, c.width, c.height);

  // Vertical rule between symbols
  ctx.strokeStyle = "rgba(255,255,255,0.06)";
  ctx.lineWidth = 1;
  SYMBOLS.forEach((_, i) => {
    if (i === 0) return;
    ctx.beginPath();
    ctx.moveTo(i * cellW, 16);
    ctx.lineTo(i * cellW, c.height - 16);
    ctx.stroke();
  });

  // Symbols
  SYMBOLS.forEach((s, i) => {
    const x = i * cellW + cellW / 2;
    const y = cellH / 2;

    if (s.isJackpot) {
      ctx.shadowColor = s.color;
      ctx.shadowBlur = 28;
      ctx.font = `900 200px ${FONT_STACK}`;
      ctx.fillStyle = s.color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(s.glyph, x, y + 14);
      // small glow ring
      ctx.shadowBlur = 0;
      ctx.strokeStyle = "rgba(255, 82, 82, 0.4)";
      ctx.lineWidth = 6;
      ctx.beginPath();
      ctx.arc(x, y, 90, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      ctx.shadowColor = s.color;
      ctx.shadowBlur = 22;
      ctx.font = `bold 150px ${FONT_STACK}`;
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(s.glyph, x, y + 8);
    }
    ctx.shadowBlur = 0;
  });

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.anisotropy = 8;
  return tex;
}

function makeButtonLabelTexture(label, color = "#a8d4ff") {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 64;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#0a0c10";
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.shadowColor = color;
  ctx.shadowBlur = 16;
  ctx.fillStyle = color;
  ctx.font = `bold 36px 'Courier New', monospace`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, c.width / 2, c.height / 2);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

function makePayoutChartTexture() {
  const c = document.createElement("canvas");
  c.width = 512;
  c.height = 768;
  const ctx = c.getContext("2d");

  ctx.fillStyle = "#06080c";
  ctx.fillRect(0, 0, c.width, c.height);

  ctx.strokeStyle = "rgba(180, 215, 255, 0.18)";
  ctx.lineWidth = 4;
  ctx.strokeRect(16, 16, c.width - 32, c.height - 32);

  ctx.fillStyle = "#82b1f7";
  ctx.font = `bold 40px 'Courier New', monospace`;
  ctx.textAlign = "center";
  ctx.fillText("PAYOUT", c.width / 2, 70);

  const rows = [
    { sym: "7  7  7", mult: "x50", color: "#ff5252", note: "JACKPOT" },
    { sym: "💎 💎 💎", mult: "x15", color: "#7ec8ff", note: "" },
    { sym: "⭐ ⭐ ⭐", mult: "x15", color: "#ffd454", note: "" },
    { sym: "🍀 🍀 🍀", mult: "x15", color: "#65e0a4", note: "" },
    { sym: "ANY 2", mult: "x2", color: "#a8d4ff", note: "" },
  ];

  ctx.textAlign = "left";
  rows.forEach((r, i) => {
    const y = 130 + i * 100;
    ctx.fillStyle = "rgba(150, 200, 255, 0.15)";
    ctx.fillRect(32, y - 30, c.width - 64, 78);
    ctx.fillStyle = r.color;
    ctx.font = `bold 38px ${FONT_STACK}`;
    ctx.fillText(r.sym, 56, y + 12);
    ctx.font = `bold 38px 'Courier New', monospace`;
    ctx.textAlign = "right";
    ctx.fillText(r.mult, c.width - 56, y + 12);
    ctx.textAlign = "left";
    if (r.note) {
      ctx.fillStyle = "rgba(255, 212, 84, 0.7)";
      ctx.font = `500 16px 'Courier New', monospace`;
      ctx.fillText(r.note, 56, y + 36);
    }
  });

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

function makeNoiseNormalTexture() {
  const size = 256;
  const data = new Uint8Array(size * size * 4);
  for (let i = 0; i < size * size; i++) {
    const v = 128 + (Math.random() - 0.5) * 22;
    data[i * 4] = v;
    data[i * 4 + 1] = v;
    data[i * 4 + 2] = 255;
    data[i * 4 + 3] = 255;
  }
  const tex = new THREE.DataTexture(data, size, size);
  tex.needsUpdate = true;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(4, 4);
  return tex;
}

/**
 * Soft radial glow texture used for halo sprites and the floor pool.
 * Returns a 256x256 canvas with a smooth white-to-transparent gradient.
 */
function makeRadialGlowTexture(softness = 0.55, falloff = 1.6) {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 256;
  const ctx = c.getContext("2d");
  const cx = c.width / 2;
  const cy = c.height / 2;
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, c.width / 2);
  grad.addColorStop(0, `rgba(255, 255, 255, ${softness})`);
  grad.addColorStop(Math.min(0.5, falloff * 0.4), `rgba(255, 255, 255, ${softness * 0.45})`);
  grad.addColorStop(1, "rgba(255, 255, 255, 0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, c.width, c.height);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

// ========================================================================
// Component
// ========================================================================

const Hero3D = ({ progressRef, chapterRef, onPartActivate }) => {
  const containerRef = useRef(null);
  const onPartActivateRef = useRef(onPartActivate);
  useEffect(() => {
    onPartActivateRef.current = onPartActivate;
  }, [onPartActivate]);
  const fallbackChapterRef = useRef(0);
  const activeChapterRef = chapterRef || fallbackChapterRef;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const width = () => container.clientWidth || window.innerWidth;
    const height = () => container.clientHeight || window.innerHeight;

    // ============================================================
    // Scene / camera / renderer
    // ============================================================
    const scene = new THREE.Scene();
    scene.background = null;

    // On narrow / portrait viewports widen the FOV so the device sits smaller
    // and chapter content has more breathing room behind it.
    const fovFor = (w, h) => (w / Math.max(h, 1) < 0.85 ? 50 : 34);
    const camera = new THREE.PerspectiveCamera(
      fovFor(width(), height()),
      width() / height(),
      0.1,
      100,
    );

    const camPositions = [
      new THREE.Vector3(0, 0.55, 8.6),
      new THREE.Vector3(0, 0.45, 8.4),
      new THREE.Vector3(0, 0.7, 10.2),
      new THREE.Vector3(0, 0.45, 8.4),
      /* CONNECT: further back so the device sits smaller behind the chapter copy */
      new THREE.Vector3(0, 0.58, 9.9),
    ];
    const camLooks = [
      new THREE.Vector3(0, 0.05, 0),
      new THREE.Vector3(-1.6, 0.05, 0),
      new THREE.Vector3(0, 0.05, 0),
      new THREE.Vector3(1.6, 0.05, 0),
      new THREE.Vector3(0, 0.08, 0),
    ];
    const camPosCurrent = camPositions[0].clone();
    const camLookCurrent = camLooks[0].clone();
    camera.position.copy(camPosCurrent);
    camera.lookAt(camLookCurrent);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width(), height());
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.domElement.style.pointerEvents = "none";
    renderer.domElement.style.touchAction = "none";
    container.appendChild(renderer.domElement);

    const pmrem = new THREE.PMREMGenerator(renderer);
    const envTexture = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = envTexture;

    // ============================================================
    // Lights
    // ============================================================
    scene.add(new THREE.AmbientLight(0xffffff, 0.18));

    const keyLight = new THREE.DirectionalLight(0xffffff, 2.1);
    keyLight.position.set(4.2, 6, 5);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(1024, 1024);
    keyLight.shadow.camera.near = 0.5;
    keyLight.shadow.camera.far = 20;
    keyLight.shadow.bias = -0.0004;
    scene.add(keyLight);

    const rimLight = new THREE.DirectionalLight(0x8fb6ff, 1.6);
    rimLight.position.set(-4.5, 1.6, -3);
    scene.add(rimLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.55);
    fillLight.position.set(-2.5, -2, 4);
    scene.add(fillLight);

    const accentLight = new THREE.PointLight(0xb8cdee, 0.6, 12);
    accentLight.position.set(0, 0.4, 1.6);
    scene.add(accentLight);

    // Win-flash light (bright in front of glass during a win)
    const winLight = new THREE.PointLight(0xffd454, 0, 6, 1.6);
    winLight.position.set(0, 0.1, 1.4);
    scene.add(winLight);

    // soft contact shadow
    const ground = new THREE.Mesh(
      new THREE.CircleGeometry(8, 64),
      new THREE.ShadowMaterial({ opacity: 0.32 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -1.8;
    ground.receiveShadow = true;
    scene.add(ground);

    // ============================================================
    // Interactive shader background
    //   - Full-screen quad rendered first (renderOrder -1, depthTest off)
    //   - FBM nebula + aurora bands, mouse-following spotlight,
    //     scroll-driven palette shift, and win-flash pulse.
    // ============================================================
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const bgUniforms = {
      uTime: { value: 0 },
      uResolution: { value: new THREE.Vector2(width(), height()) },
      uMouse: { value: new THREE.Vector2() },
      uProgress: { value: 0 },
      uPulse: { value: 0 },
      uReduced: { value: prefersReducedMotion ? 1 : 0 },
      // Chapter-driven palette (lerped JS-side toward CHAPTER_PALETTES[chapter])
      uChapter: { value: 0 },
      uPaletteBase: { value: new THREE.Color(CHAPTER_PALETTES[0].base) },
      uPaletteMid:  { value: new THREE.Color(CHAPTER_PALETTES[0].mid)  },
      uPaletteAcc:  { value: new THREE.Color(CHAPTER_PALETTES[0].acc)  },
      uPaletteWarm: { value: new THREE.Color(CHAPTER_PALETTES[0].warm) },
    };

    const bgMaterial = new THREE.ShaderMaterial({
      uniforms: bgUniforms,
      depthTest: false,
      depthWrite: false,
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          // Full-screen quad in clip space, bypassing camera matrices.
          gl_Position = vec4(position.xy, 1.0, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;
        varying vec2 vUv;
        uniform float uTime;
        uniform vec2  uResolution;
        uniform vec2  uMouse;
        uniform float uProgress;
        uniform float uPulse;
        uniform float uReduced;
        uniform float uChapter;       // smooth float 0..4
        uniform vec3  uPaletteBase;
        uniform vec3  uPaletteMid;
        uniform vec3  uPaletteAcc;
        uniform vec3  uPaletteWarm;

        float hash21(vec2 p) {
          p = fract(p * vec2(123.34, 456.21));
          p += dot(p, p + 78.233);
          return fract(p.x * p.y);
        }

        float vnoise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          float a = hash21(i);
          float b = hash21(i + vec2(1.0, 0.0));
          float c = hash21(i + vec2(0.0, 1.0));
          float d = hash21(i + vec2(1.0, 1.0));
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
        }

        float fbm(vec2 p) {
          float v = 0.0;
          float a = 0.5;
          for (int i = 0; i < 5; i++) {
            v += a * vnoise(p);
            p = p * 2.04 + vec2(11.3, 7.7);
            a *= 0.5;
          }
          return v;
        }

        // Triangular kernel: peaks 1 at \`center\`, falls to 0 at \`center +/- 1\`
        float wgt(float c, float center) {
          return max(0.0, 1.0 - abs(c - center));
        }

        // Anti-aliased thin line around x = 0 with half-width w
        float lineAA(float x, float w) {
          return 1.0 - smoothstep(0.0, w, abs(x));
        }

        // ---- 0 INTRO: warp streaks rushing outward + aurora curtain ------
        vec3 bgIntro(vec2 p, float T, float n) {
          float r = length(p);
          float a = atan(p.y, p.x);
          float u = a / 6.2831853 * 96.0;
          float sector = floor(u);
          float sa = fract(u) - 0.5;
          float h = hash21(vec2(sector, 7.0));
          float speed = 0.5 + h * 0.6;
          float along = fract(r * 1.4 - T * 0.35 * speed + h * 7.0);
          float head = smoothstep(0.0, 0.06, along) * smoothstep(0.6, 0.1, along);
          float width = 1.0 - smoothstep(0.0, 0.10 + 0.08 * r, abs(sa));
          float streak = head * width * step(0.45, h) * smoothstep(0.2, 0.7, r);
          float aur = smoothstep(0.5, 1.0, sin(p.y * 3.4 + n * 4.2 + T * 0.12))
                    * smoothstep(-0.2, 0.5, p.y);
          return uPaletteAcc * streak * 0.85
               + mix(uPaletteAcc, uPaletteWarm, 0.35) * aur * 0.28;
        }

        // ---- 1 PROFILE: blueprint drafting sheet -------------------------
        vec3 bgProfile(vec2 p, float T) {
          vec2 g = p * 10.0;
          vec2 gf = abs(fract(g) - 0.5);
          float minor = smoothstep(0.46, 0.5, max(gf.x, gf.y));      // thin lines
          vec2 gM = abs(fract(g * 0.2) - 0.5);
          float major = smoothstep(0.486, 0.5, max(gM.x, gM.y));     // every 5th, bolder
          vec2 c = p - vec2(-0.62 + sin(T * 0.2) * 0.04, 0.12 + cos(T * 0.17) * 0.03);
          float rc = length(c);
          float arcs = lineAA(fract(rc * 3.5) - 0.5, 0.035) * smoothstep(1.3, 0.15, rc);
          float ring = lineAA(rc - 0.42, 0.006) + lineAA(rc - 0.44, 0.004) * 0.6;
          float ticks = step(0.86, fract(atan(c.y, c.x) / 6.2831853 * 36.0 + T * 0.05))
                      * lineAA(rc - 0.46, 0.02);
          float cross = (lineAA(c.x, 0.004) * step(0.5, fract(c.y * 10.0 - T * 0.3))
                       + lineAA(c.y, 0.004) * step(0.5, fract(c.x * 10.0 + T * 0.3)))
                      * smoothstep(1.1, 0.2, rc);
          float band = exp(-abs(p.y - (fract(T * 0.08) * 1.8 - 0.9)) * 12.0);
          vec3 col = uPaletteAcc * (minor * 0.08 + major * 0.22 + arcs * 0.4
                                  + ring * 0.9 + ticks * 0.9 + cross * 0.5);
          col += uPaletteAcc * band * (0.16 + 0.4 * minor);
          return col;
        }

        // ---- 2 SKILLS: hex circuit with ring pulses ----------------------
        float hexDist(vec2 p) {
          p = abs(p);
          return max(dot(p, normalize(vec2(1.0, 1.7320508))), p.x);
        }
        vec4 hexCoords(vec2 uv) {
          vec2 rr = vec2(1.0, 1.7320508);
          vec2 hh = rr * 0.5;
          vec2 a = mod(uv, rr) - hh;
          vec2 b = mod(uv - hh, rr) - hh;
          vec2 gv = dot(a, a) < dot(b, b) ? a : b;
          return vec4(gv, uv - gv);
        }
        vec3 bgSkills(vec2 p, float T) {
          float S = 7.5;
          vec2 scroll = vec2(0.0, T * 0.06);
          vec4 hc = hexCoords(p * S + scroll);
          float d = hexDist(hc.xy);
          float lines = 1.0 - smoothstep(0.0, 0.05, abs(d - 0.47));
          float inner = smoothstep(0.5, 0.0, d);
          vec2 cellP = (hc.zw - scroll) / S;
          float pulse = smoothstep(0.86, 1.0, sin(length(cellP) * 5.0 - T * 1.6));
          float h = hash21(hc.zw);
          float flick = step(0.94, fract(h + T * 0.05)) * (0.5 + 0.5 * sin(T * 6.0 + h * 40.0));
          float a = atan(p.y, p.x);
          float spokes = lineAA(fract(a / 6.2831853 * 6.0 + T * 0.02) - 0.5, 0.01)
                       * smoothstep(0.2, 0.9, length(p)) * 0.25;
          return uPaletteAcc * (lines * 0.2 + (pulse * 0.38 + flick * 0.5) * inner)
               + uPaletteWarm * spokes;
        }

        // ---- 3 SERVICES: projector beams + scrolling film strips ---------
        vec3 bgServices(vec2 p, float T, float n) {
          vec2 dir = vec2(0.82, 0.57);
          float across = dot(p, vec2(-dir.y, dir.x));
          float along = dot(p, dir);
          float beams = 0.0;
          for (int i = 0; i < 3; i++) {
            float fi = float(i);
            float c = -0.55 + fi * 0.5 + sin(T * 0.25 + fi * 2.1) * 0.1;
            beams += exp(-abs(across - c) * (7.0 + fi * 2.0)) * (0.55 + 0.45 * n);
          }
          beams *= smoothstep(-1.4, 0.3, along) * 0.5;
          float film = 0.0;
          for (int i = 0; i < 3; i++) {
            float fi = float(i);
            float yC = -0.66 + fi * 0.66;
            float dy = abs(p.y - yC);
            float dirS = mod(fi, 2.0) < 1.0 ? 1.0 : -1.0;
            float x = p.x + T * (0.05 + fi * 0.03) * dirS;
            float holeRow = step(0.14, dy) * (1.0 - step(0.175, dy));
            float xs = fract(x * 8.0);
            float hole = holeRow * step(0.28, xs) * (1.0 - step(0.72, xs));
            float band = 1.0 - step(0.13, dy);
            float frames = band * lineAA(fract(x * 1.5) - 0.5, 0.01);
            float border = lineAA(dy - 0.2, 0.005) + lineAA(dy - 0.125, 0.004) * 0.6;
            film += hole * 0.55 + frames * 0.4 + border * 0.35;
          }
          return mix(uPaletteAcc, uPaletteWarm, 0.45) * beams + uPaletteAcc * film * 0.45;
        }

        // ---- 4 CONNECT: sonar pings + radar sweep + oscilloscope ---------
        vec3 bgConnect(vec2 p, float T) {
          float r = length(p);
          float a = atan(p.y, p.x);
          float rings = 0.0;
          for (int i = 0; i < 3; i++) {
            float ph = fract(T * 0.09 + float(i) / 3.0);
            rings += lineAA(r - ph * 1.7, 0.012 + ph * 0.03) * (1.0 - ph);
          }
          float circ = lineAA(fract(r * 4.0) - 0.5, 0.02) * 0.3 * smoothstep(1.7, 0.2, r);
          float spokes = lineAA(fract(a / 6.2831853 * 12.0) - 0.5, 0.015) * 0.22
                       * smoothstep(0.1, 0.5, r) * smoothstep(1.7, 0.6, r);
          float sweep = pow(fract(a / 6.2831853 - T * 0.06), 10.0) * smoothstep(1.4, 0.15, r) * 0.55;
          float waves = 0.0;
          for (int i = 0; i < 4; i++) {
            float fi = float(i);
            float yC = -0.3 - fi * 0.11;
            float env = 0.5 + 0.5 * sin(p.x * 1.3 + fi * 1.7 + T * 0.3);
            float w = sin(p.x * (5.0 + fi * 1.5) + T * (0.9 + fi * 0.25)) * 0.06 * env;
            waves += lineAA(p.y - yC - w, 0.006) * (1.0 - fi * 0.18);
          }
          return uPaletteAcc * (rings * 0.9 + circ + spokes + sweep)
               + mix(uPaletteAcc, uPaletteWarm, 0.5) * waves;
        }

        void main() {
          vec2 uv = vUv;
          vec2 p = uv - 0.5;
          float aspect = uResolution.x / max(uResolution.y, 1.0);
          p.x *= aspect;

          float t = uTime * (uReduced > 0.5 ? 0.012 : 0.055);

          // Domain-warped FBM nebula (constant base layer for all chapters)
          vec2 q = p * 1.35 + vec2(t, -t * 0.6);
          float n1 = fbm(q + uMouse * 0.18);
          vec2 r  = vec2(
            fbm(q + vec2(1.7, 9.2) + n1 * 1.6),
            fbm(q + vec2(8.3, 2.8) - n1 * 1.6)
          );
          float n = fbm(q + r * 1.9);

          // Chapter palette (uniforms are JS-side lerped toward target chapter)
          vec3 col = mix(uPaletteBase, uPaletteMid, smoothstep(0.18, 0.65, n));
          col = mix(col, uPaletteAcc,  smoothstep(0.55, 0.92, n) * 0.5);
          col = mix(col, uPaletteWarm, smoothstep(0.78, 0.97, n) * (0.10 + 0.18 * uProgress));

          // Per-chapter overlay weights (cross-fade between adjacent overlays)
          float w0 = wgt(uChapter, 0.0);
          float w1 = wgt(uChapter, 1.0);
          float w2 = wgt(uChapter, 2.0);
          float w3 = wgt(uChapter, 3.0);
          float w4 = wgt(uChapter, 4.0);

          // Per-chapter scene motifs. Each is gated on its weight so at most
          // two (during a cross-fade) are evaluated per fragment; the branch
          // is uniform across the screen so it costs nothing on the GPU.
          vec2 pm = p + uMouse * vec2(aspect, 1.0) * 0.05;   // subtle parallax
          float T = uTime * (uReduced > 0.5 ? 0.15 : 1.0);
          vec3 motif = vec3(0.0);
          if (w0 > 0.001) motif += w0 * bgIntro(pm, T, n);
          if (w1 > 0.001) motif += w1 * bgProfile(pm, T);
          if (w2 > 0.001) motif += w2 * bgSkills(pm, T);
          if (w3 > 0.001) motif += w3 * bgServices(pm, T, n);
          if (w4 > 0.001) motif += w4 * bgConnect(pm, T);
          col += motif * (0.8 + 0.2 * uProgress);

          // Mouse-following spotlight (chapter-tinted)
          vec2 m = uMouse * vec2(aspect, 1.0) * 0.55;
          float md = length(p - m);
          float glow = exp(-md * 2.4) * 0.55;
          vec3 glowCol = mix(uPaletteAcc * 1.6, uPaletteWarm, uProgress);
          col += glowCol * glow * (1.0 + uPulse * 1.6);

          // Twinkling pixel-stars (cheap)
          vec2 g = floor(uv * uResolution.xy * 0.55 + uMouse * 6.0);
          float starSeed = hash21(g);
          float star = step(0.9975, starSeed);
          float twinkle = 0.55 + 0.45 * sin(uTime * 2.4 + starSeed * 31.4);
          col += vec3(0.85, 0.92, 1.0) * star * twinkle * 0.55;

          // Centre-dim pool so the device reads prominently in front
          float centre = smoothstep(0.0, 0.55, length(p));
          col *= mix(0.55, 1.0, centre);

          // Soft outer vignette
          float vig = smoothstep(1.55, 0.55, length(p));
          col *= mix(0.55, 1.0, vig);

          // Win-flash gentle wash
          col += vec3(1.0, 0.85, 0.55) * uPulse * 0.08;

          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });

    const bgGeometry = new THREE.PlaneGeometry(2, 2);
    const bgMesh = new THREE.Mesh(bgGeometry, bgMaterial);
    bgMesh.frustumCulled = false;
    bgMesh.renderOrder = -1;
    scene.add(bgMesh);

    // ============================================================
    // Depth layers — far stars, mid-ground gas clouds, near dust.
    // These give the scene parallax that the user can FEEL when the
    // camera shifts between chapter shots, on top of the 2D shader bg.
    // ============================================================

    // -- Far parallax star field --------------------------------------
    const FAR_STAR_COUNT = 700;
    const farStarsGeo = new THREE.BufferGeometry();
    {
      const pos = new Float32Array(FAR_STAR_COUNT * 3);
      const sz  = new Float32Array(FAR_STAR_COUNT);
      for (let i = 0; i < FAR_STAR_COUNT; i++) {
        // Spread in a wide hemispherical shell behind the device
        const r = 22 + Math.random() * 28;
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1) * 0.9;
        pos[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta);
        pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.6;
        pos[i * 3 + 2] = -Math.abs(r * Math.cos(phi)) - 6;
        sz[i] = 0.5 + Math.random() * 1.8;
      }
      farStarsGeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
      farStarsGeo.setAttribute("aSize",    new THREE.BufferAttribute(sz, 1));
    }
    const farStarsMat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(0xc8d8ff) },
        uPixelRatio: { value: renderer.getPixelRatio() },
      },
      vertexShader: `
        attribute float aSize;
        uniform float uTime;
        uniform float uPixelRatio;
        varying float vTwinkle;
        void main() {
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mv;
          float seed = position.x * 0.31 + position.y * 0.17 + position.z * 0.07;
          vTwinkle = 0.55 + 0.45 * sin(uTime * 1.4 + seed);
          gl_PointSize = aSize * uPixelRatio * (180.0 / -mv.z);
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        varying float vTwinkle;
        void main() {
          vec2 c = gl_PointCoord - 0.5;
          float d = length(c);
          float core = smoothstep(0.5, 0.0, d);
          float halo = smoothstep(0.5, 0.18, d) * 0.35;
          gl_FragColor = vec4(uColor, (core + halo) * vTwinkle * 0.85);
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
    });
    const farStars = new THREE.Points(farStarsGeo, farStarsMat);
    farStars.renderOrder = -0.5; // above bg quad, below scene meshes
    farStars.frustumCulled = false;
    scene.add(farStars);

    // -- Mid-ground tinted gas clouds (additive billboards) -----------
    const gasTex = makeRadialGlowTexture(0.85, 1.6);
    const gasSprites = [];
    const GAS_COUNT = 7;
    for (let i = 0; i < GAS_COUNT; i++) {
      const mat = new THREE.SpriteMaterial({
        map: gasTex,
        color: new THREE.Color(CHAPTER_PALETTES[0].acc),
        blending: THREE.AdditiveBlending,
        transparent: true,
        opacity: 0.10 + Math.random() * 0.07,
        depthWrite: false,
        depthTest: false,
      });
      const s = new THREE.Sprite(mat);
      const angle = (i / GAS_COUNT) * Math.PI * 2 + Math.random() * 0.4;
      const r = 4.5 + Math.random() * 3.5;
      s.position.set(
        Math.cos(angle) * r,
        Math.sin(angle) * 1.6 + (Math.random() - 0.5) * 0.6,
        -3 - Math.random() * 5,
      );
      const scale = 5 + Math.random() * 5;
      s.scale.set(scale, scale, 1);
      s.renderOrder = -0.4;
      s.userData = {
        phase: Math.random() * Math.PI * 2,
        bobAmp: 0.4 + Math.random() * 0.5,
        bobSpd: 0.3 + Math.random() * 0.3,
        baseY: s.position.y,
        baseX: s.position.x,
      };
      scene.add(s);
      gasSprites.push(s);
    }

    // -- Per-chapter floating 3D objects ------------------------------
    // Each chapter owns its own InstancedMesh: distinct geometry + material
    // so the foreground feels different in every section. Instances are
    // distributed across three z-layers (far / mid / near) with matching
    // size variance and per-layer parallax strength — strong depth cue.
    // Opacity cross-fades between chapters for seamless transitions.
    const CHAPTER_OBJ_COUNT = 28;

    // Geometry per chapter — all roughly unit-scale so per-instance scale
    // can layer them cleanly.
    const chapterGeoms = [
      new THREE.IcosahedronGeometry(0.18, 0),         // 0 INTRO    - cosmic shards
      new THREE.TorusGeometry(0.22, 0.06, 12, 32),    // 1 PROFILE  - soft rings
      new THREE.OctahedronGeometry(0.20, 0),          // 2 SKILLS   - tech crystals (wireframe)
      new THREE.CapsuleGeometry(0.10, 0.28, 6, 12),   // 3 SERVICES - glass capsules
      new THREE.DodecahedronGeometry(0.22, 0),        // 4 CONNECT  - faceted gems
    ];

    // Materials per chapter — different look-and-feel per section.
    const _palAcc = (i) => new THREE.Color(CHAPTER_PALETTES[i].acc);
    const chapterMats = [
      // 0 INTRO — emissive cyan glass shards
      new THREE.MeshStandardMaterial({
        color: _palAcc(0).clone().multiplyScalar(0.35),
        emissive: _palAcc(0),
        emissiveIntensity: 0.85,
        metalness: 0.7,
        roughness: 0.22,
        transparent: true,
        opacity: 1,
        depthWrite: false,
      }),
      // 1 PROFILE — soft mint rings, low metalness
      new THREE.MeshStandardMaterial({
        color: _palAcc(1).clone().multiplyScalar(0.4),
        emissive: _palAcc(1),
        emissiveIntensity: 0.6,
        metalness: 0.25,
        roughness: 0.45,
        transparent: true,
        opacity: 0,
        depthWrite: false,
      }),
      // 2 SKILLS — wireframe amber octahedrons (tech feel)
      new THREE.MeshBasicMaterial({
        color: _palAcc(2),
        wireframe: true,
        transparent: true,
        opacity: 0,
        depthWrite: false,
      }),
      // 3 SERVICES — glossy rose capsules
      new THREE.MeshStandardMaterial({
        color: _palAcc(3).clone().multiplyScalar(0.45),
        emissive: _palAcc(3),
        emissiveIntensity: 0.7,
        metalness: 0.3,
        roughness: 0.28,
        transparent: true,
        opacity: 0,
        depthWrite: false,
      }),
      // 4 CONNECT — high-metalness violet gems with sharp facets
      new THREE.MeshStandardMaterial({
        color: _palAcc(4).clone().multiplyScalar(0.3),
        emissive: _palAcc(4),
        emissiveIntensity: 0.9,
        metalness: 0.9,
        roughness: 0.18,
        transparent: true,
        opacity: 0,
        depthWrite: false,
      }),
    ];

    // Per-instance state arrays per chapter. Matrices computed every frame
    // from these (drift + bob + spin + parallax).
    const chapterMeshes = [];
    const chapterStates = [];
    for (let c = 0; c < 5; c++) {
      const mesh = new THREE.InstancedMesh(
        chapterGeoms[c],
        chapterMats[c],
        CHAPTER_OBJ_COUNT,
      );
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.frustumCulled = false;
      mesh.renderOrder = 4;
      scene.add(mesh);
      chapterMeshes.push(mesh);

      const states = [];
      for (let i = 0; i < CHAPTER_OBJ_COUNT; i++) {
        // Three z-layers: 0=far (small/slow), 1=mid, 2=near (large/fast).
        // Roughly equal counts across layers.
        const layer = i % 3;
        const z = layer === 0 ? -8 + Math.random() * 4
                : layer === 1 ? -3 + Math.random() * 3
                :                0 + Math.random() * 2.2;
        // Bias x off-center so the device gets clear sight lines.
        const sideX = Math.random() < 0.5 ? -1 : 1;
        const xOff  = 3.2 + Math.random() * 6;
        const baseScale =
          layer === 0 ? 0.45 + Math.random() * 0.35 :
          layer === 1 ? 0.7  + Math.random() * 0.5  :
                        1.05 + Math.random() * 0.7;
        states.push({
          basePos: new THREE.Vector3(
            sideX * xOff,
            (Math.random() - 0.5) * 9,
            z,
          ),
          rotAxis: new THREE.Vector3(
            Math.random() - 0.5,
            Math.random() - 0.5,
            Math.random() - 0.5,
          ).normalize(),
          rotSpeed:  0.15 + Math.random() * 0.6,
          rotPhase:  Math.random() * Math.PI * 2,
          driftSpeed: 0.12 + Math.random() * 0.28,
          driftPhase: Math.random() * 12, // initial Y offset
          bobAmp:    0.25 + Math.random() * 0.6,
          bobSpd:    0.4  + Math.random() * 0.55,
          bobPhase:  Math.random() * Math.PI * 2,
          scale:     baseScale,
          layer,
        });
      }
      chapterStates.push(states);
    }

    // Working colors / dummy for per-frame matrix updates (no allocation)
    const _palTargetBase = new THREE.Color();
    const _palTargetMid  = new THREE.Color();
    const _palTargetAcc  = new THREE.Color();
    const _palTargetWarm = new THREE.Color();
    const _objDummy = new THREE.Object3D();
    const _objQuat  = new THREE.Quaternion();
    // Cursor ray (camera origin + unprojected NDC dir) — drives per-instance
    // proximity attraction so chapter floaters feel hand-touchable.
    const _cursorRayOrigin = new THREE.Vector3();
    const _cursorRayDir    = new THREE.Vector3();

    // ============================================================
    // Textures
    // ============================================================
    const noiseNormal = makeNoiseNormalTexture();
    const brandTex = makeBrandTexture();
    const marqueeTex = makeMarqueeTexture();
    const payoutChartTex = makePayoutChartTexture();
    const { tex: displayTex, draw: drawDisplay } = makeDisplay();
    const reelStripTex = makeReelStripTexture();
    const reelTextures = [reelStripTex, reelStripTex.clone(), reelStripTex.clone()];
    reelTextures.forEach((t, i) => {
      t.colorSpace = THREE.SRGBColorSpace;
      t.wrapS = THREE.RepeatWrapping;
      t.wrapT = THREE.ClampToEdgeWrapping;
      t.anisotropy = 8;
      // initial offsets so each reel starts on a different symbol.
      // The +0.5 puts a cell *center* on the front of the cylinder (u=0),
      // matching the half-cell offset baked into spin landings.
      t.offset.x = (i + 0.5) / N_SYMBOLS;
      t.needsUpdate = true;
    });

    const buttonLabels = {
      spin: makeButtonLabelTexture("SPIN", "#9ff5c8"),
      bet: makeButtonLabelTexture("BET", "#ffd454"),
      credit: makeButtonLabelTexture("CREDIT", "#a8d4ff"),
    };

    const glowTex = makeRadialGlowTexture(0.85, 1.4);
    const floorPoolTex = makeRadialGlowTexture(0.55, 1.2);

    // ============================================================
    // Materials
    // ============================================================
    const brushed = new THREE.MeshPhysicalMaterial({
      color: 0x252830,
      metalness: 0.9,
      roughness: 0.28,
      clearcoat: 0.6,
      clearcoatRoughness: 0.22,
      envMapIntensity: 1.25,
      normalMap: noiseNormal,
      normalScale: new THREE.Vector2(0.18, 0.18),
    });
    const dark = new THREE.MeshStandardMaterial({
      color: 0x141619,
      metalness: 0.7,
      roughness: 0.42,
    });
    const blackPaint = new THREE.MeshStandardMaterial({
      color: 0x0c0d11,
      metalness: 0.4,
      roughness: 0.55,
    });
    const chrome = new THREE.MeshPhysicalMaterial({
      color: 0xbcc0c8,
      metalness: 1,
      roughness: 0.14,
      clearcoat: 0.7,
      clearcoatRoughness: 0.16,
      envMapIntensity: 1.4,
    });
    const chromeBright = new THREE.MeshPhysicalMaterial({
      color: 0xe6e9ef,
      metalness: 1,
      roughness: 0.08,
      clearcoat: 0.85,
      clearcoatRoughness: 0.1,
      envMapIntensity: 1.6,
    });
    const screwMat = new THREE.MeshStandardMaterial({
      color: 0x6c7280,
      metalness: 1,
      roughness: 0.4,
    });
    const glass = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      metalness: 0,
      roughness: 0.05,
      transmission: 0.95,
      thickness: 0.2,
      ior: 1.4,
      transparent: true,
      opacity: 0.4,
    });
    const beaconGlass = new THREE.MeshPhysicalMaterial({
      color: 0xfff4d6,
      metalness: 0,
      roughness: 0.1,
      transmission: 0.9,
      thickness: 0.15,
      ior: 1.45,
      emissive: 0xffd454,
      emissiveIntensity: 0.6,
      transparent: true,
      opacity: 0.55,
    });
    const brandMat = new THREE.MeshStandardMaterial({
      map: brandTex,
      emissiveMap: brandTex,
      emissive: 0x2c3a55,
      emissiveIntensity: 0.6,
      metalness: 0.45,
      roughness: 0.5,
    });
    const marqueeMat = new THREE.MeshStandardMaterial({
      map: marqueeTex,
      emissiveMap: marqueeTex,
      emissive: 0x4a5fb0,
      emissiveIntensity: 1.4,
      metalness: 0.2,
      roughness: 0.55,
    });
    const payoutChartMat = new THREE.MeshStandardMaterial({
      map: payoutChartTex,
      emissiveMap: payoutChartTex,
      emissive: 0x274780,
      emissiveIntensity: 0.55,
      metalness: 0.2,
      roughness: 0.6,
    });
    const displayMat = new THREE.MeshStandardMaterial({
      map: displayTex,
      emissiveMap: displayTex,
      emissive: 0x68a8ff,
      emissiveIntensity: 1.6,
      metalness: 0,
      roughness: 0.5,
    });
    const reelMats = reelTextures.map(
      (tex) =>
        new THREE.MeshStandardMaterial({
          map: tex,
          emissiveMap: tex,
          emissive: 0x4470a0,
          emissiveIntensity: 0.55,
          metalness: 0.4,
          roughness: 0.55,
        }),
    );
    const reelEndCapMat = new THREE.MeshStandardMaterial({
      color: 0x0d1014,
      metalness: 0.7,
      roughness: 0.45,
    });
    const buttonLabelMats = {
      spin: new THREE.MeshStandardMaterial({
        map: buttonLabels.spin,
        emissiveMap: buttonLabels.spin,
        emissive: 0x65e0a4,
        emissiveIntensity: 1.0,
        metalness: 0,
        roughness: 0.6,
      }),
      bet: new THREE.MeshStandardMaterial({
        map: buttonLabels.bet,
        emissiveMap: buttonLabels.bet,
        emissive: 0xffd454,
        emissiveIntensity: 1.0,
        metalness: 0,
        roughness: 0.6,
      }),
      credit: new THREE.MeshStandardMaterial({
        map: buttonLabels.credit,
        emissiveMap: buttonLabels.credit,
        emissive: 0x82b1f7,
        emissiveIntensity: 1.0,
        metalness: 0,
        roughness: 0.6,
      }),
    };
    const buttonMatSpin = new THREE.MeshStandardMaterial({
      color: 0x123322,
      metalness: 0.6,
      roughness: 0.32,
      emissive: 0x65e0a4,
      emissiveIntensity: 0.7,
    });
    const buttonMatBet = new THREE.MeshStandardMaterial({
      color: 0x332b12,
      metalness: 0.6,
      roughness: 0.32,
      emissive: 0xffb04a,
      emissiveIntensity: 0.6,
    });
    const buttonMatCredit = new THREE.MeshStandardMaterial({
      color: 0x12233a,
      metalness: 0.6,
      roughness: 0.32,
      emissive: 0x6b8fc4,
      emissiveIntensity: 0.6,
    });
    const buttonRingSpin = new THREE.MeshStandardMaterial({
      color: 0x14241c,
      metalness: 0.6,
      roughness: 0.4,
      emissive: 0x65e0a4,
      emissiveIntensity: 1.2,
    });
    const buttonRingBet = new THREE.MeshStandardMaterial({
      color: 0x261d10,
      metalness: 0.6,
      roughness: 0.4,
      emissive: 0xffb04a,
      emissiveIntensity: 1.2,
    });
    const buttonRingCredit = new THREE.MeshStandardMaterial({
      color: 0x12141a,
      metalness: 0.6,
      roughness: 0.4,
      emissive: 0x82b1f7,
      emissiveIntensity: 1.2,
    });
    const indicatorMatRed = new THREE.MeshStandardMaterial({
      color: 0x110608,
      metalness: 0.4,
      roughness: 0.5,
      emissive: 0xff7a5c,
      emissiveIntensity: 1.4,
    });
    const indicatorMatGreen = new THREE.MeshStandardMaterial({
      color: 0x051108,
      metalness: 0.4,
      roughness: 0.5,
      emissive: 0x65e0a4,
      emissiveIntensity: 1.4,
    });
    const indicatorMatYellow = new THREE.MeshStandardMaterial({
      color: 0x111005,
      metalness: 0.4,
      roughness: 0.5,
      emissive: 0xffd454,
      emissiveIntensity: 1.4,
    });
    const winLineMat = new THREE.MeshStandardMaterial({
      color: 0x080a0e,
      metalness: 0,
      roughness: 0.6,
      emissive: 0xffd454,
      emissiveIntensity: 0.4,
    });
    const pedestalMat = new THREE.MeshStandardMaterial({
      color: 0x0a0c10,
      metalness: 0.7,
      roughness: 0.32,
    });
    const trayMat = new THREE.MeshStandardMaterial({
      color: 0x16191f,
      metalness: 0.6,
      roughness: 0.38,
    });
    const ventMat = new THREE.MeshStandardMaterial({
      color: 0x05070a,
      metalness: 0.5,
      roughness: 0.6,
    });
    const sparkleBaseMat = new THREE.MeshStandardMaterial({
      color: 0xffd454,
      emissive: 0xffd454,
      emissiveIntensity: 2.6,
      transparent: true,
      opacity: 0,
    });

    // Cyan neon trim — thin glowing strips along key seams.
    const cyanTrimMat = new THREE.MeshStandardMaterial({
      color: 0x081623,
      emissive: 0x82b1f7,
      emissiveIntensity: 1.6,
      metalness: 0.2,
      roughness: 0.45,
    });
    // Backlit cabinet glow — softer, sits behind reels and around the body.
    const cabinetGlowMat = new THREE.MeshStandardMaterial({
      color: 0x05101e,
      emissive: 0x4a7bd1,
      emissiveIntensity: 1.05,
      metalness: 0.0,
      roughness: 0.65,
    });
    // Halo sprite material — additive cyan glow billboard.
    const haloSpriteMat = new THREE.SpriteMaterial({
      map: glowTex,
      color: 0x82b1f7,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      opacity: 0.55,
    });
    const beaconHaloMat = new THREE.SpriteMaterial({
      map: glowTex,
      color: 0xffd454,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      opacity: 0.65,
    });
    // Soft floor pool — fades to transparent at the rim, sits above shadow.
    const floorPoolMat = new THREE.MeshBasicMaterial({
      map: floorPoolTex,
      color: 0x6f9ddc,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      opacity: 0.45,
    });

    const allMaterials = [
      brushed, dark, blackPaint, chrome, chromeBright, screwMat, glass,
      beaconGlass, brandMat, marqueeMat, payoutChartMat, displayMat,
      ...reelMats, reelEndCapMat,
      buttonMatSpin, buttonMatBet, buttonMatCredit,
      buttonRingSpin, buttonRingBet, buttonRingCredit,
      buttonLabelMats.spin, buttonLabelMats.bet, buttonLabelMats.credit,
      indicatorMatRed, indicatorMatGreen, indicatorMatYellow,
      winLineMat, pedestalMat, trayMat, ventMat, sparkleBaseMat,
      cyanTrimMat, cabinetGlowMat, haloSpriteMat, beaconHaloMat, floorPoolMat,
    ];

    const geos = [];
    const tg = (g) => { geos.push(g); return g; };
    const make = (geometry, material, [x, y, z], rot, userData = {}) => {
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(x, y, z);
      if (rot) mesh.rotation.set(rot[0] || 0, rot[1] || 0, rot[2] || 0);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData = userData;
      return mesh;
    };

    // ============================================================
    // Build the device
    // ============================================================
    const device = new THREE.Group();
    scene.add(device);

    // Composition helpers — keep meshes as direct children of `device`
    // so the flat traversal in the explosion system continues to work.
    const add = (geometry, material, pos, rot, userData) =>
      device.add(make(geometry, material, pos, rot, userData));
    // Mirror a part across the cabinet centerline (X axis).
    const addPair = (geometry, material, [x, y, z], rot, userData) => {
      add(geometry, material, [-x, y, z], rot, userData);
      add(geometry, material, [ x, y, z], rot, userData);
    };
    // Build a rectangular chrome trim frame (top/bottom + left/right strips).
    const frameRect = (mat, [cx, cy, cz], w, h, thick, depth) => {
      const horizGeo = tg(new RoundedBoxGeometry(w, thick, depth, 2, thick * 0.3));
      const vertGeo  = tg(new RoundedBoxGeometry(thick, h, depth, 2, thick * 0.3));
      add(horizGeo, mat, [cx, cy + h / 2 - thick / 2, cz]);
      add(horizGeo, mat, [cx, cy - h / 2 + thick / 2, cz]);
      add(vertGeo,  mat, [cx - w / 2 + thick / 2, cy, cz]);
      add(vertGeo,  mat, [cx + w / 2 - thick / 2, cy, cz]);
    };

    // ---- Pedestal (3-tier) -----------------------------------------------
    const pedestalTop = make(
      tg(new RoundedBoxGeometry(3.6, 0.14, 1.8, 4, 0.04)),
      pedestalMat,
      [0, -1.18, 0],
    );
    device.add(pedestalTop);
    device.add(make(
      tg(new RoundedBoxGeometry(3.8, 0.14, 1.95, 4, 0.04)),
      blackPaint,
      [0, -1.32, 0],
    ));
    device.add(make(
      tg(new RoundedBoxGeometry(4.0, 0.10, 2.1, 4, 0.04)),
      pedestalMat,
      [0, -1.42, 0],
    ));
    // pedestal accent lights (chrome strip)
    device.add(make(
      tg(new RoundedBoxGeometry(3.5, 0.018, 1.74, 2, 0.005)),
      chrome,
      [0, -1.245, 0],
    ));
    // Under-cabinet glow — thin cyan emissive strip wrapping the bottom tier.
    // Reads as a premium casino under-light, makes the device feel like it
    // floats on a pool of light.
    device.add(make(
      tg(new RoundedBoxGeometry(3.92, 0.012, 2.02, 2, 0.004)),
      cyanTrimMat,
      [0, -1.37, 0],
    ));

    // ---- Chrome cabinet feet (LatheGeometry) -----------------------------
    // Sculpted legs at the four pedestal corners give the device a proper
    // "machine-on-feet" silhouette and let the floor pool peek through.
    // Profile: flared base disk → narrow waist → top flange.
    {
      const footProfile = [
        new THREE.Vector2(0.135, 0.000),
        new THREE.Vector2(0.142, 0.012),
        new THREE.Vector2(0.130, 0.022),
        new THREE.Vector2(0.086, 0.058),
        new THREE.Vector2(0.078, 0.142),
        new THREE.Vector2(0.090, 0.206),
        new THREE.Vector2(0.122, 0.246),
        new THREE.Vector2(0.135, 0.272),
        new THREE.Vector2(0.135, 0.292),
        new THREE.Vector2(0.001, 0.292),
      ];
      const footGeo = tg(new THREE.LatheGeometry(footProfile, 28));
      const footPositions = [
        [-1.78,  0.87], [ 1.78,  0.87],
        [-1.78, -0.87], [ 1.78, -0.87],
      ];
      for (const [fx, fz] of footPositions) {
        device.add(make(footGeo, chromeBright, [fx, -1.78, fz]));
      }
    }

    // ---- Front kickplate crest (ExtrudeGeometry) -------------------------
    // Sculpted chrome badge on the pedestal front face. Uses an extruded
    // shield shape with a beveled edge so light catches the rim.
    {
      const crestShape = new THREE.Shape();
      crestShape.moveTo(-0.20, -0.060);
      crestShape.lineTo( 0.20, -0.060);
      crestShape.bezierCurveTo(0.245, -0.040, 0.245,  0.040, 0.20, 0.060);
      crestShape.lineTo(-0.20,  0.060);
      crestShape.bezierCurveTo(-0.245, 0.040, -0.245, -0.040, -0.20, -0.060);
      const crestGeo = tg(new THREE.ExtrudeGeometry(crestShape, {
        depth: 0.014,
        bevelEnabled: true,
        bevelThickness: 0.006,
        bevelSize: 0.004,
        bevelSegments: 3,
      }));
      device.add(make(crestGeo, chromeBright, [0, -1.42, 1.052]));
      // Inset cyan accent dot in the center of the crest
      device.add(make(
        tg(new THREE.SphereGeometry(0.014, 12, 12)),
        cyanTrimMat,
        [0, -1.42, 1.078],
      ));
    }

    // ---- Body main panel -------------------------------------------------
    const body = make(
      tg(new RoundedBoxGeometry(3.0, 1.85, 1.4, 5, 0.08)),
      brushed,
      [0, 0, 0],
      null,
      { partName: "body", pulseable: true },
    );
    device.add(body);

    // Top accent strip
    device.add(make(
      tg(new RoundedBoxGeometry(2.6, 0.18, 1.2, 4, 0.04)),
      chrome,
      [0, 1.02, 0],
    ));
    device.add(make(
      tg(new RoundedBoxGeometry(0.62, 0.04, 0.62, 4, 0.012)),
      chromeBright,
      [0, 1.13, 0],
    ));

    // Brand plate on top panel front
    const brandPlate = new THREE.Mesh(tg(new THREE.PlaneGeometry(2.0, 0.4)), brandMat);
    brandPlate.position.set(0, 1.06, 0.61);
    device.add(brandPlate);

    // Bottom plate
    device.add(make(
      tg(new RoundedBoxGeometry(2.85, 0.14, 1.3, 4, 0.04)),
      dark,
      [0, -1.0, 0],
    ));

    // ---- Bezel (recessed face) -------------------------------------------
    device.add(make(
      tg(new RoundedBoxGeometry(2.6, 1.1, 0.05, 4, 0.02)),
      blackPaint,
      [0, 0.16, 0.71],
    ));

    // Chrome bezel frame around glass area
    const bezelFrameOuter = 1.18;
    const bezelFrameInner = 0.96;
    const bezelTop = make(
      tg(new RoundedBoxGeometry(2.55, 0.06, 0.04, 2, 0.012)),
      chromeBright,
      [0, bezelFrameOuter / 2 + 0.16 - 0.05, 0.745],
    );
    device.add(bezelTop);
    device.add(make(
      tg(new RoundedBoxGeometry(2.55, 0.06, 0.04, 2, 0.012)),
      chromeBright,
      [0, -bezelFrameInner / 2 + 0.16 - 0.05, 0.745],
    ));
    addPair(
      tg(new RoundedBoxGeometry(0.06, 1.0, 0.04, 2, 0.012)),
      chromeBright,
      [1.27, 0.16, 0.745],
    );

    // ---- Cyan neon trim (frames the bezel + body seams) ------------------
    // Inner glow rim sitting just inside the chrome bezel — reads as backlit
    // edge-light around the playfield.
    frameRect(cyanTrimMat, [0, 0.16, 0.755], 2.42, 0.94, 0.012, 0.012);

    // Body seams: thin glow strips under the marquee and above the pedestal.
    device.add(make(
      tg(new RoundedBoxGeometry(2.55, 0.012, 0.04, 2, 0.004)),
      cyanTrimMat,
      [0, 0.92, 0.71],
    ));
    device.add(make(
      tg(new RoundedBoxGeometry(2.55, 0.012, 0.04, 2, 0.004)),
      cyanTrimMat,
      [0, -0.92, 0.71],
    ));
    // Side rail vertical glow strips — sit on the seam between body and rail
    // for premium piping detail visible head-on.
    addPair(
      tg(new RoundedBoxGeometry(0.014, 1.45, 0.018, 2, 0.004)),
      cyanTrimMat,
      [1.51, 0, 0.69],
    );

    // ---- Side speaker grilles (InstancedMesh perforations) ---------------
    // Premium detail: black housing inset between the chrome bezel strip
    // and the side rail, faced with a real perforated dot grille. Each
    // dot is one instance of a tiny sphere — efficient and crisp at any
    // zoom level. Hex-offset rows give it a proper audio-grille feel.
    {
      const grilleHousingGeo = tg(new RoundedBoxGeometry(0.20, 0.80, 0.05, 2, 0.012));
      const grilleEdgeGeo    = tg(new RoundedBoxGeometry(0.21, 0.81, 0.012, 2, 0.006));
      const grilleFaceGeo    = tg(new THREE.PlaneGeometry(0.18, 0.76));
      const dotGeo           = tg(new THREE.SphereGeometry(0.011, 10, 10));

      const grilleFaceMat = new THREE.MeshStandardMaterial({
        color: 0x05060a,
        metalness: 0.25,
        roughness: 0.85,
      });
      const dotMat = new THREE.MeshStandardMaterial({
        color: 0x191c24,
        metalness: 0.7,
        roughness: 0.4,
      });
      allMaterials.push(grilleFaceMat, dotMat);

      const dotsCols = 4;
      const dotsRows = 18;
      const dotPitchX = 0.040;
      const dotPitchY = 0.040;
      const dotOffsetX = -((dotsCols - 1) * dotPitchX) / 2;
      const dotOffsetY = -((dotsRows - 1) * dotPitchY) / 2;
      const dummyDot = new THREE.Object3D();

      for (const sideX of [-1.395, 1.395]) {
        device.add(make(grilleHousingGeo, blackPaint, [sideX, 0.16, 0.708]));
        device.add(make(grilleEdgeGeo, chrome,        [sideX, 0.16, 0.716]));
        device.add(make(grilleFaceGeo, grilleFaceMat, [sideX, 0.16, 0.738]));

        // Perforation dots — InstancedMesh anchored at the grille center
        // so the explosion logic transports it as one coherent block.
        const dotMesh = new THREE.InstancedMesh(
          dotGeo, dotMat, dotsCols * dotsRows,
        );
        dotMesh.castShadow = true;
        dotMesh.position.set(sideX, 0.16, 0.748);
        let idx = 0;
        for (let r = 0; r < dotsRows; r++) {
          const rowShift = (r % 2) * (dotPitchX * 0.5);
          for (let c = 0; c < dotsCols; c++) {
            const dx = dotOffsetX + c * dotPitchX + rowShift;
            const dy = dotOffsetY + r * dotPitchY;
            dummyDot.position.set(dx, dy, 0);
            dummyDot.updateMatrix();
            dotMesh.setMatrixAt(idx++, dummyDot.matrix);
          }
        }
        dotMesh.instanceMatrix.needsUpdate = true;
        device.add(dotMesh);
      }
    }

    // ---- LCD display strip (above the reels) -----------------------------
    device.add(make(
      tg(new RoundedBoxGeometry(2.4, 0.32, 0.06, 2, 0.02)),
      blackPaint,
      [0, 0.56, 0.74],
    ));
    const displayMesh = make(
      tg(new THREE.PlaneGeometry(2.32, 0.26)),
      displayMat,
      [0, 0.56, 0.778],
    );
    device.add(displayMesh);

    // ---- Reel housing (inset cavity behind glass) ------------------------
    // Backlit panel — gives the reels a glowing cabinet backdrop instead of
    // pure black, matching the cyan accent palette.
    device.add(make(
      tg(new THREE.BoxGeometry(2.4, 0.7, 0.04)),
      cabinetGlowMat,
      [0, 0.1, 0.42],
    ));

    // ---- Reels (3 cylinders, with chrome end-caps) -----------------------
    const reelGeo = tg(new THREE.CylinderGeometry(0.32, 0.32, 0.52, 56));
    const reelEndCapGeo = tg(new THREE.CylinderGeometry(0.34, 0.34, 0.04, 56));
    const reelMeshes = [];
    for (let i = -1; i <= 1; i++) {
      const reel = make(reelGeo, reelMats[i + 1], [i * 0.78, 0.1, 0.55], [0, 0, Math.PI / 2], {
        partName: `reel-${i + 1}`,
        interactive: true,
        action: "reel-click",
        index: i + 1,
        label: "Spin",
      });
      reelMeshes.push(reel);
      device.add(reel);
      // end caps to hide the open cylinder ends
      device.add(make(reelEndCapGeo, reelEndCapMat, [i * 0.78 - 0.27, 0.1, 0.55], [0, 0, Math.PI / 2]));
      device.add(make(reelEndCapGeo, reelEndCapMat, [i * 0.78 + 0.27, 0.1, 0.55], [0, 0, Math.PI / 2]));
    }

    // Chrome dividers between reels
    const dividerGeo = tg(new RoundedBoxGeometry(0.04, 0.7, 0.05, 2, 0.012));
    addPair(dividerGeo, chromeBright, [0.39, 0.1, 0.745]);

    // Win-line (horizontal LED strip across the reels, slim)
    const winLine = make(
      tg(new THREE.BoxGeometry(2.32, 0.018, 0.005)),
      winLineMat,
      [0, 0.1, 0.78],
    );
    device.add(winLine);

    // Glass pane
    device.add(make(
      tg(new THREE.BoxGeometry(2.4, 0.7, 0.04)),
      glass,
      [0, 0.1, 0.74],
    ));

    // ---- Side rails (chrome, with cooling vents) --------------------------
    const railGeo = tg(new RoundedBoxGeometry(0.12, 1.62, 1.32, 4, 0.04));
    addPair(railGeo, chrome, [1.52, 0, 0]);
    // recessed vent block
    const ventBlockGeo = tg(new RoundedBoxGeometry(0.04, 1.2, 0.7, 2, 0.012));
    addPair(ventBlockGeo, ventMat, [1.5, 0, 0]);
    // horizontal vent slats
    const slatGeo = tg(new THREE.BoxGeometry(0.05, 0.025, 0.65));
    for (let i = 0; i < 9; i++) {
      addPair(slatGeo, chromeBright, [1.5, -0.5 + i * 0.13, 0]);
    }

    // ---- Coin slot insert (top-right of body face) ------------------------
    const coinSlotPlate = make(
      tg(new RoundedBoxGeometry(0.55, 0.16, 0.04, 2, 0.012)),
      chrome,
      [1.0, -0.4, 0.72],
    );
    device.add(coinSlotPlate);
    device.add(make(
      tg(new THREE.BoxGeometry(0.4, 0.04, 0.08)),
      ventMat,
      [1.0, -0.4, 0.732],
    ));
    // tiny LED next to the coin slot
    device.add(make(
      tg(new THREE.SphereGeometry(0.018, 12, 12)),
      indicatorMatGreen,
      [0.78, -0.4, 0.745],
    ));

    // ---- Money tray (front bottom protrusion) -----------------------------
    device.add(make(
      tg(new RoundedBoxGeometry(2.0, 0.18, 0.5, 4, 0.04)),
      trayMat,
      [0, -0.86, 0.94],
    ));
    device.add(make(
      tg(new RoundedBoxGeometry(1.85, 0.04, 0.42, 2, 0.012)),
      blackPaint,
      [0, -0.79, 0.95],
    ));
    // inner tray glow strip
    device.add(make(
      tg(new RoundedBoxGeometry(1.7, 0.005, 0.005, 1, 0.001)),
      winLineMat,
      [0, -0.78, 1.16],
    ));

    // ---- Control panel (sloped strip below glass) -------------------------
    const panelGeo = tg(new RoundedBoxGeometry(2.6, 0.36, 0.5, 4, 0.04));
    const controlPanel = make(panelGeo, blackPaint, [0, -0.55, 0.62], [-0.32, 0, 0]);
    device.add(controlPanel);
    // chrome trim
    device.add(make(
      tg(new RoundedBoxGeometry(2.55, 0.014, 0.5, 1, 0.005)),
      chromeBright,
      [0, -0.39, 0.74],
      [-0.32, 0, 0],
    ));

    // ---- Buttons (3) with rings + small label tag above each -------------
    // The control panel is rotated PANEL_TILT rad around X; buttons sit
    // flush on its tilted face. Constants are hoisted out of the loop so
    // the trig only runs once and reading the layout is straightforward.
    const PANEL_TILT = -0.32;
    const PANEL_BASE_Y = -0.55;
    const PANEL_BASE_Z = 0.62;
    const BTN_PANEL_OFF = 0.18; // shift along panel's tilted Y from its center
    const BTN_Y = PANEL_BASE_Y + Math.cos(PANEL_TILT) * BTN_PANEL_OFF + 0.02;
    const BTN_Z = PANEL_BASE_Z + Math.sin(PANEL_TILT) * BTN_PANEL_OFF + 0.18;
    const LBL_OFF = 0.02; // label sits just above panel center
    const LBL_Y = PANEL_BASE_Y + Math.cos(PANEL_TILT) * LBL_OFF + 0.18;
    const LBL_Z = PANEL_BASE_Z + Math.sin(PANEL_TILT) * LBL_OFF + 0.34;
    const BTN_ROT = [Math.PI / 2 + PANEL_TILT, 0, 0];

    // Panel-local "into the cabinet" axis (negative of the tilted panel
    // normal). Each pressed button is offset along this so it sinks flush
    // and rebounds via spring physics for a tactile arcade feel.
    const buttonPressAxis = new THREE.Vector3(0, Math.sin(PANEL_TILT), -Math.cos(PANEL_TILT));

    // Shared geometry across the three buttons.
    const btnGeo = tg(new THREE.CylinderGeometry(0.14, 0.14, 0.1, 32));
    const btnRingGeo = tg(new THREE.TorusGeometry(0.16, 0.018, 16, 48));
    const btnWellGeo = tg(new THREE.TorusGeometry(0.175, 0.024, 12, 36));
    const btnLabelGeo = tg(new THREE.PlaneGeometry(0.32, 0.08));

    const btnSpecs = [
      {
        x: -0.7, label: "SPIN", action: "spin",
        body: buttonMatSpin, ring: buttonRingSpin, lblMat: buttonLabelMats.spin,
      },
      {
        x: 0.0, label: "BET", action: "bet",
        body: buttonMatBet, ring: buttonRingBet, lblMat: buttonLabelMats.bet,
      },
      {
        x: 0.7, label: "CREDIT", action: "credit",
        body: buttonMatCredit, ring: buttonRingCredit, lblMat: buttonLabelMats.credit,
      },
    ];
    const buttonByAction = {};
    btnSpecs.forEach((spec) => {
      // Recessed black socket — sits between button and chrome ring so
      // each button reads as inset into the panel rather than bolted on.
      add(btnWellGeo, blackPaint, [spec.x, BTN_Y, BTN_Z + 0.025], BTN_ROT);
      const btn = make(
        btnGeo, spec.body,
        [spec.x, BTN_Y, BTN_Z],
        BTN_ROT,
        {
          partName: `button-${spec.label}`,
          interactive: true,
          action: spec.action,
          label: spec.label,
          pulseable: true,
        },
      );
      device.add(btn);
      // Per-button press spring (critically damped — quick sink, no jitter).
      btn.userData.pressSpring = { position: 0, velocity: 0 };
      buttonByAction[spec.action] = btn;
      add(btnRingGeo, spec.ring, [spec.x, BTN_Y, BTN_Z + 0.05], BTN_ROT);
      // Small label plate above the button (etched into panel)
      const labelPlane = new THREE.Mesh(btnLabelGeo, spec.lblMat);
      labelPlane.position.set(spec.x, LBL_Y, LBL_Z);
      labelPlane.rotation.x = PANEL_TILT;
      device.add(labelPlane);
    });

    // ---- Coin slot ledge (on top of body, decorative) --------------------
    device.add(make(
      tg(new RoundedBoxGeometry(2.2, 0.08, 0.1, 2, 0.012)),
      chrome,
      [0, -0.22, 0.74],
    ));

    // ---- Corner bumpers + screws -----------------------------------------
    const cornerGeo = tg(new RoundedBoxGeometry(0.22, 0.22, 1.5, 4, 0.04));
    const cornerPositions = [
      [-1.45, 0.86, 0],
      [1.45, 0.86, 0],
      [-1.45, -0.86, 0],
      [1.45, -0.86, 0],
    ];
    cornerPositions.forEach((p) => device.add(make(cornerGeo, dark, p)));

    const screwGeo = tg(new THREE.CylinderGeometry(0.026, 0.026, 0.04, 12));
    const screwSpots = [
      [-1.45, 0.86, 0.71], [1.45, 0.86, 0.71],
      [-1.45, -0.86, 0.71], [1.45, -0.86, 0.71],
      [-1.45, 0.86, -0.71], [1.45, 0.86, -0.71],
      [-1.45, -0.86, -0.71], [1.45, -0.86, -0.71],
    ];
    screwSpots.forEach((p) =>
      device.add(make(screwGeo, screwMat, p, [Math.PI / 2, 0, 0])),
    );

    // ---- Marquee header (illuminated brand panel above the body) ---------
    const marqueeBack = make(
      tg(new RoundedBoxGeometry(3.4, 0.85, 0.32, 4, 0.06)),
      blackPaint,
      [0, 1.86, 0],
    );
    device.add(marqueeBack);
    // chrome trim frame around marquee — top/bottom + left/right
    frameRect(chromeBright, [0, 1.86, 0], 3.45, 0.96, 0.06, 0.34);
    // illuminated front face
    const marqueeFront = new THREE.Mesh(
      tg(new THREE.PlaneGeometry(3.2, 0.7)),
      marqueeMat,
    );
    marqueeFront.position.set(0, 1.86, 0.165);
    device.add(marqueeFront);
    // small marquee status LEDs along bottom
    for (let i = -3; i <= 3; i++) {
      device.add(make(
        tg(new THREE.SphereGeometry(0.022, 10, 10)),
        i === 0 ? indicatorMatYellow : (i % 2 === 0 ? indicatorMatGreen : indicatorMatRed),
        [i * 0.42, 1.46, 0.18],
        null,
        { partName: `marquee-led-${i + 3}`, blinkPhase: i * 0.7 },
      ));
    }

    // marquee mounting struts (visually attaches header to body)
    addPair(
      tg(new RoundedBoxGeometry(0.18, 0.36, 0.4, 2, 0.04)),
      chrome,
      [1.0, 1.3, 0],
    );

    // ---- Beacon dome on top of marquee -----------------------------------
    const beaconBaseMesh = make(
      tg(new THREE.CylinderGeometry(0.18, 0.22, 0.08, 32)),
      chrome,
      [0, 2.36, 0],
    );
    device.add(beaconBaseMesh);
    // Sculpted dome profile: subtle neck just above the base, gentle swell
    // mid-height, then a soft shoulder collapsing to a point. Reads as a
    // turned-glass beacon rather than a plain hemisphere.
    const beaconDomeProfile = [
      new THREE.Vector2(0.180, 0.000),
      new THREE.Vector2(0.180, 0.018),
      new THREE.Vector2(0.168, 0.038),
      new THREE.Vector2(0.162, 0.060),
      new THREE.Vector2(0.180, 0.090),
      new THREE.Vector2(0.180, 0.118),
      new THREE.Vector2(0.158, 0.144),
      new THREE.Vector2(0.118, 0.168),
      new THREE.Vector2(0.060, 0.184),
      new THREE.Vector2(0.001, 0.190),
    ];
    const beaconDome = make(
      tg(new THREE.LatheGeometry(beaconDomeProfile, 36)),
      beaconGlass,
      [0, 2.40, 0],
      null,
      { partName: "beacon-dome" },
    );
    device.add(beaconDome);
    // Chrome neck collar where the dome meets its base — catches highlights.
    device.add(make(
      tg(new THREE.TorusGeometry(0.182, 0.012, 12, 36)),
      chromeBright,
      [0, 2.41, 0],
      [Math.PI / 2, 0, 0],
    ));
    // tiny inner bulb
    const beaconBulb = make(
      tg(new THREE.SphereGeometry(0.07, 16, 16)),
      indicatorMatYellow,
      [0, 2.42, 0],
      null,
      { partName: "beacon-bulb" },
    );
    device.add(beaconBulb);
    const beaconLight = new THREE.PointLight(0xffd454, 0, 4, 1.5);
    beaconLight.position.set(0, 2.42, 0);
    device.add(beaconLight);

    // ---- Top indicator chase lights ---------------------------------------
    // Bigger illuminated lenses with chrome bezels + additive halos. Driven
    // by a dedicated chase animation in the loop so they pulse in sequence
    // and react to game state (idle / spinning / win / jackpot).
    const indicatorGeo = tg(new THREE.SphereGeometry(0.062, 20, 20));
    const indicatorRingGeo = tg(new THREE.TorusGeometry(0.082, 0.014, 14, 28));
    const indicatorBaseColors = [0x82b1f7, 0x65e0a4, 0xffd454, 0xff7a8e];
    const indicatorXs = [-0.55, -0.18, 0.19, 0.56];
    const indicators = [];
    indicatorXs.forEach((x, i) => {
      const baseHex = indicatorBaseColors[i];
      const mat = new THREE.MeshStandardMaterial({
        color: 0x080a0e,
        metalness: 0.4,
        roughness: 0.5,
        emissive: baseHex,
        emissiveIntensity: 1.0,
      });
      allMaterials.push(mat);

      const ind = make(indicatorGeo, mat, [x, 1.05, 0.66], null, {
        partName: `indicator-${i}`,
        basePhase: i / 4,
        customAnim: true,
      });
      device.add(ind);

      // chrome bezel ring around the lens
      device.add(make(
        indicatorRingGeo, chromeBright, [x, 1.05, 0.66], [Math.PI / 2, 0, 0],
      ));

      // additive halo sprite — child of the lens so it follows during
      // explosion + chapter motion.
      const haloMat = new THREE.SpriteMaterial({
        map: glowTex,
        color: baseHex,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        opacity: 0.32,
      });
      allMaterials.push(haloMat);
      const halo = new THREE.Sprite(haloMat);
      halo.scale.set(0.46, 0.46, 1);
      halo.position.set(0, 0, 0.02);
      ind.add(halo);

      indicators.push({
        mesh: ind,
        material: mat,
        haloMat,
        halo,
        baseColor: new THREE.Color(baseHex),
        basePhase: i / 4,
      });
    });

    // ---- Payout chart on the LEFT side of body ---------------------------
    const payoutPlate = new THREE.Mesh(
      tg(new THREE.PlaneGeometry(0.7, 1.05)),
      payoutChartMat,
    );
    payoutPlate.position.set(-1.555, 0, 0);
    payoutPlate.rotation.y = -Math.PI / 2;
    device.add(payoutPlate);
    // chrome bezel around it
    [
      [-0.355, 0, 0.535], [-0.355, 0, -0.535],
      [-0.355, 0.55, 0], [-0.355, -0.55, 0],
    ].forEach((p, i) => {
      const horiz = i >= 2;
      device.add(make(
        tg(new THREE.BoxGeometry(0.02, horiz ? 0.04 : 1.1, horiz ? 0.74 : 0.04)),
        chromeBright,
        [-1.555, p[1], p[2]],
      ));
    });

    // ============================================================
    // LEVER ASSEMBLY (anchored properly to the body)
    // ============================================================
    // Strategy: put a chrome housing on the right side of the body that
    // protrudes outward. Inside the housing's outer end is a pivot Group
    // that holds the arm + ball. Rotating the pivot around X swings the
    // arm forward (toward camera and downward). The arm appears to come
    // OUT of the housing because the housing visually overlaps the pivot.

    // Housing fixed onto the body (not part of the pivot)
    const leverHousing = make(
      tg(new THREE.CylinderGeometry(0.15, 0.16, 0.5, 32)),
      chrome,
      [1.78, 0.0, 0],
      [0, 0, Math.PI / 2],
      { partName: "lever-housing" },
    );
    device.add(leverHousing);
    // Inner cap (where the arm emerges)
    const leverHousingCap = make(
      tg(new THREE.CylinderGeometry(0.16, 0.16, 0.04, 32)),
      chromeBright,
      [1.6, 0.0, 0],
      [0, 0, Math.PI / 2],
    );
    device.add(leverHousingCap);
    // Decorative flange ring at the body-side junction — turned chrome
    // collar that finishes the seam between cap and housing barrel.
    add(
      tg(new THREE.TorusGeometry(0.18, 0.022, 16, 32)),
      chromeBright,
      [1.62, 0, 0],
      [0, Math.PI / 2, 0],
    );
    // Mid-housing chrome band — breaks the long cylinder into two visual
    // segments and catches highlights as the device rotates.
    add(
      tg(new THREE.TorusGeometry(0.16, 0.012, 12, 32)),
      chromeBright,
      [1.78, 0, 0],
      [0, Math.PI / 2, 0],
    );
    const leverHousingOuterCap = make(
      tg(new THREE.CylinderGeometry(0.17, 0.17, 0.04, 32)),
      chromeBright,
      [2.04, 0.0, 0],
      [0, 0, Math.PI / 2],
    );
    device.add(leverHousingOuterCap);
    // small support strut between housing and body
    add(
      tg(new RoundedBoxGeometry(0.16, 0.18, 0.08, 2, 0.012)),
      chrome,
      [1.6, 0.0, 0],
    );

    // Pivot group sits at the OUTER end of the housing where the arm
    // visually emerges. The arm's "base" is at the pivot, extending up.
    const leverPivot = new THREE.Group();
    leverPivot.position.set(2.04, 0.0, 0);
    device.add(leverPivot);

    // Arm: cylinder, base at pivot (Y=0 within pivot), tip at Y=0.7
    const leverArm = make(
      tg(new THREE.CylinderGeometry(0.038, 0.045, 0.7, 24)),
      chromeBright,
      [0, 0.35, 0],
      null,
      { 
        partName: "lever-arm",
        interactive: true,
        action: "spin",
        label: "Pull lever",
        pulseable: true,
      },
    );
    leverPivot.add(leverArm);
    // Sleeve at the base for visual mass
    leverPivot.add(make(
      tg(new THREE.CylinderGeometry(0.07, 0.07, 0.08, 24)),
      chromeBright,
      [0, 0.04, 0],
    ));
    // Two chrome detail bands on the arm — break the plain cylinder into
    // segments and catch highlights as the lever swings. Slightly less
    // polished than the arm itself so they read as distinct collars.
    leverPivot.add(make(
      tg(new THREE.TorusGeometry(0.046, 0.008, 12, 24)),
      chrome,
      [0, 0.20, 0],
      [Math.PI / 2, 0, 0],
    ));
    leverPivot.add(make(
      tg(new THREE.TorusGeometry(0.041, 0.006, 12, 24)),
      chrome,
      [0, 0.52, 0],
      [Math.PI / 2, 0, 0],
    ));
    // Ball at the top
    const leverBall = make(
      tg(new THREE.SphereGeometry(0.115, 32, 32)),
      buttonRingCredit,
      [0, 0.78, 0],
      null,
      {
        partName: "lever-ball",
        interactive: true,
        action: "spin",
        label: "Pull lever",
        pulseable: true,
      },
    );
    leverPivot.add(leverBall);
    // Rim around the ball for chrome detail
    leverPivot.add(make(
      tg(new THREE.TorusGeometry(0.115, 0.014, 12, 24)),
      chromeBright,
      [0, 0.78, 0],
      [Math.PI / 2, 0, 0],
    ));

    // ============================================================
    // Sculptural style accents
    // ============================================================
    // Crown finial (LatheGeometry) — turned chrome ornament topping the
    // beacon for a regal silhouette. Profile: collar → swell → spire.
    {
      const finialProfile = [
        new THREE.Vector2(0.001, 0.000),
        new THREE.Vector2(0.072, 0.012),
        new THREE.Vector2(0.064, 0.040),
        new THREE.Vector2(0.046, 0.066),
        new THREE.Vector2(0.060, 0.092),
        new THREE.Vector2(0.040, 0.130),
        new THREE.Vector2(0.018, 0.170),
        new THREE.Vector2(0.001, 0.210),
      ];
      add(tg(new THREE.LatheGeometry(finialProfile, 32)),
        chromeBright, [0, 2.595, 0]);
      // Tiny cyan jewel set into the spire tip
      add(tg(new THREE.IcosahedronGeometry(0.022, 0)),
        cyanTrimMat, [0, 2.815, 0]);
    }

    // Beveled jewel gems at the marquee corners (ExtrudeGeometry diamonds) —
    // sit just inside the chrome trim, well clear of the brand text. Bevel
    // catches highlights so the cabinet face glints as the device rotates.
    {
      const diamond = new THREE.Shape();
      diamond.moveTo(0, 0.060);
      diamond.lineTo(0.045, 0);
      diamond.lineTo(0, -0.060);
      diamond.lineTo(-0.045, 0);
      diamond.lineTo(0, 0.060);
      const jewelGeo = tg(new THREE.ExtrudeGeometry(diamond, {
        depth: 0.018,
        bevelEnabled: true,
        bevelThickness: 0.010,
        bevelSize: 0.012,
        bevelSegments: 4,
      }));
      // 4 corners — inside the chrome trim frame (x≈±1.55, y≈1.55 / 2.17).
      addPair(jewelGeo, indicatorMatYellow, [1.55, 2.17, 0.18]);
      addPair(jewelGeo, indicatorMatRed,    [1.55, 1.55, 0.18]);
    }

    // Faceted icosahedron accents flanking the brand plate — emissive blue
    // gems that read as "set into" the cabinet badge.
    {
      const gemGeo = tg(new THREE.IcosahedronGeometry(0.052, 0));
      const gemMat = new THREE.MeshStandardMaterial({
        color: 0x0a1422,
        metalness: 0.3,
        roughness: 0.25,
        emissive: 0x82b1f7,
        emissiveIntensity: 1.2,
      });
      allMaterials.push(gemMat);
      addPair(gemGeo, gemMat, [1.12, 1.06, 0.612], [0, 0, Math.PI / 5]);
      // chrome bezel cup behind each gem
      addPair(
        tg(new THREE.CylinderGeometry(0.072, 0.072, 0.018, 24)),
        chromeBright,
        [1.12, 1.06, 0.602],
        [Math.PI / 2, 0, 0],
      );
    }

    // Hex-perforation back-vent (InstancedMesh) — visible when the device
    // rotates; gives the cabinet a real "machine has guts" feel.
    {
      const ventDotGeo = tg(new THREE.SphereGeometry(0.018, 8, 8));
      const ventDotMat = new THREE.MeshStandardMaterial({
        color: 0x0a0c10,
        metalness: 0.6,
        roughness: 0.5,
      });
      allMaterials.push(ventDotMat);
      // Recessed black housing behind the body for the perforation panel.
      add(
        tg(new RoundedBoxGeometry(2.2, 1.2, 0.04, 4, 0.04)),
        blackPaint,
        [0, 0.05, -0.71],
      );
      const cols = 24, rows = 12;
      const pitchX = 0.078, pitchY = 0.078;
      const offX = -((cols - 1) * pitchX) / 2;
      const offY = -((rows - 1) * pitchY) / 2;
      const hexMesh = new THREE.InstancedMesh(
        ventDotGeo, ventDotMat, cols * rows,
      );
      hexMesh.castShadow = true;
      hexMesh.receiveShadow = true;
      hexMesh.position.set(0, 0.05, -0.69);
      const dummy = new THREE.Object3D();
      let idx = 0;
      for (let r = 0; r < rows; r++) {
        const rowShift = (r % 2) * (pitchX * 0.5);
        for (let c = 0; c < cols; c++) {
          dummy.position.set(offX + c * pitchX + rowShift, offY + r * pitchY, 0);
          dummy.updateMatrix();
          hexMesh.setMatrixAt(idx++, dummy.matrix);
        }
      }
      hexMesh.instanceMatrix.needsUpdate = true;
      device.add(hexMesh);
    }

    // Sculpted rosette caps on the four corner bumpers — turned chrome
    // discs at the front/back ends of each depth-running bumper so the
    // cabinet edges read as capped/finished rather than blunt extrusions.
    // Lathe profile (Y axis) is reoriented to face along Z by rotating ±X.
    {
      // Profile starts inside the bumper (y < 0) and curves outward — this
      // way the lathed surface has no Y=0 annulus that could z-fight with
      // the bumper end face, and the cap reads as flush-set rather than
      // bolted-on.
      const rosetteProfile = [
        new THREE.Vector2(0.001, -0.010),
        new THREE.Vector2(0.108, -0.006),
        new THREE.Vector2(0.118,  0.010),
        new THREE.Vector2(0.092,  0.024),
        new THREE.Vector2(0.072,  0.036),
        new THREE.Vector2(0.001,  0.042),
      ];
      const rosetteGeo = tg(new THREE.LatheGeometry(rosetteProfile, 24));
      const bumperX = 1.45;
      const bumperY = 0.86;
      const bumperZ = 0.75; // half of bumper depth (1.5)
      // Push each cap clearly outward along its facing axis so no part of
      // the lathed surface sits coplanar with the bumper end face (z-fighting).
      const NUDGE = 0.014;
      // Front caps face +Z (rotate -90° around X), back caps face -Z (+90°).
      for (const sy of [-1, 1]) {
        addPair(rosetteGeo, chromeBright,
          [bumperX, sy * bumperY,  bumperZ + NUDGE],
          [-Math.PI / 2, 0, 0]);
        addPair(rosetteGeo, chromeBright,
          [bumperX, sy * bumperY, -bumperZ - NUDGE],
          [ Math.PI / 2, 0, 0]);
      }
    }

    // ============================================================
    // Atmospheric polish: halo sprites + floor pool
    // ============================================================
    // Soft cyan glow behind the device — anchored to the scene so it stays
    // put even as the device rotates, giving a sense of ambient backlight.
    const deviceHalo = new THREE.Sprite(haloSpriteMat);
    deviceHalo.position.set(0, 0.3, -1.6);
    deviceHalo.scale.set(7.5, 5.2, 1);
    scene.add(deviceHalo);

    // Beacon halo — child of the bulb so it follows during explosion + spin.
    const beaconHalo = new THREE.Sprite(beaconHaloMat);
    beaconHalo.scale.set(1.4, 1.4, 1);
    beaconBulb.add(beaconHalo);

    // Floor pool — soft cyan disc above the shadow plane, gives the cabinet
    // a "floating on light" feel and ties it to the shader background.
    const floorPool = new THREE.Mesh(
      tg(new THREE.CircleGeometry(3.2, 64)),
      floorPoolMat,
    );
    floorPool.rotation.x = -Math.PI / 2;
    floorPool.position.set(0, -1.78, 0);
    scene.add(floorPool);

    // ============================================================
    // PARTICLE SYSTEM (win sparkles)
    // ============================================================
    const PARTICLE_COUNT = 80;
    const particleGeo = tg(new THREE.SphereGeometry(0.04, 8, 8));
    const particles = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const mat = sparkleBaseMat.clone();
      mat.transparent = true;
      mat.opacity = 0;
      const m = new THREE.Mesh(particleGeo, mat);
      m.visible = false;
      device.add(m);
      particles.push({
        mesh: m,
        material: mat,
        vel: new THREE.Vector3(),
        life: 0,
        maxLife: 0,
      });
      allMaterials.push(mat);
    }

    function spawnSparkles(count, jackpot = false) {
      let spawned = 0;
      for (const p of particles) {
        if (spawned >= count) break;
        if (p.life > 0) continue;
        spawned++;
        p.mesh.position.set(
          (Math.random() - 0.5) * 2.0,
          0.1 + (Math.random() - 0.4) * 0.4,
          0.78,
        );
        p.vel.set(
          (Math.random() - 0.5) * 3.5,
          Math.random() * 3.6 + 1.6,
          Math.random() * 1.4 + 0.4,
        );
        p.life = 1.4 + Math.random() * 0.9;
        p.maxLife = p.life;
        p.mesh.visible = true;
        p.material.opacity = 1;
        if (jackpot) {
          // Confetti style: hue-cycled
          const hue = Math.random();
          p.material.color.setHSL(hue, 1, 0.55);
          p.material.emissive.setHSL(hue, 1, 0.55);
          p.material.emissiveIntensity = 3.4;
        } else {
          // Gold sparkles
          p.material.color.setHex(0xffd454);
          p.material.emissive.setHex(0xffd454);
          p.material.emissiveIntensity = 2.6;
        }
      }
    }

    function updateParticles(dt) {
      const grav = -7;
      for (const p of particles) {
        if (p.life <= 0) {
          if (p.mesh.visible) p.mesh.visible = false;
          continue;
        }
        p.life -= dt;
        p.vel.y += grav * dt;
        p.mesh.position.x += p.vel.x * dt;
        p.mesh.position.y += p.vel.y * dt;
        p.mesh.position.z += p.vel.z * dt;
        p.material.opacity = Math.max(0, p.life / p.maxLife);
        if (p.life <= 0) p.mesh.visible = false;
      }
    }

    // ============================================================
    // GAME STATE & LOGIC
    // ============================================================
    const game = {
      credits: 100,
      bet: 10,
      lastWin: 0,
      status: "IDLE", // IDLE | SPINNING | WIN | LOSE | JACKPOT | INSERT
      reels: reelMats.map((mat, i) => ({
        material: mat,
        progress: 0.13 * i, // visible offset
        startProgress: 0,
        endProgress: 0,
        spinStartTime: 0,
        spinDuration: 0,
        targetSymbol: 0,
        currentSymbol: 0,
        state: "idle", // idle | queued | spinning | stopped
      })),
      winFlash: 0, // 0..1, decays after a win
      pendingResolveAt: 0,
      revealedAt: 0,
    };

    function refreshDisplay() {
      const showStatus =
        game.credits < game.bet && game.status === "IDLE" ? "INSERT" : game.status;
      drawDisplay({
        status: showStatus,
        credits: game.credits,
        bet: game.bet,
        lastWin: game.lastWin,
      });
      displayTex.needsUpdate = true;
    }
    refreshDisplay();

    function evaluateOutcome() {
      const symbols = game.reels.map((r) => r.currentSymbol);
      const [a, b, c] = symbols;
      let winAmt = 0;
      let outcome = "LOSE";
      if (a === b && b === c) {
        const isJp = SYMBOLS[a]?.isJackpot;
        winAmt = isJp ? game.bet * 50 : game.bet * 15;
        outcome = isJp ? "JACKPOT" : "WIN";
      } else if (a === b || b === c || a === c) {
        winAmt = game.bet * 2;
        outcome = "WIN";
      }
      return { outcome, winAmt };
    }

    function startSpin() {
      if (game.status === "SPINNING") return;
      if (game.credits < game.bet) {
        game.status = "INSERT";
        refreshDisplay();
        onPartActivateRef.current?.({ kind: "lose", message: "Insert credits" });
        return;
      }
      game.credits -= game.bet;
      game.lastWin = 0;
      game.status = "SPINNING";

      // Pre-decide outcome (random target symbols), with a slight bias
      // so the device sometimes lands on matches for a satisfying demo.
      const r = Math.random();
      let targets;
      if (r < 0.05) {
        // jackpot
        targets = [0, 0, 0];
      } else if (r < 0.18) {
        // three of a kind (non-jackpot)
        const sym = 1 + Math.floor(Math.random() * (N_SYMBOLS - 1));
        targets = [sym, sym, sym];
      } else if (r < 0.42) {
        // two of a kind
        const sym = Math.floor(Math.random() * N_SYMBOLS);
        const other = (sym + 1 + Math.floor(Math.random() * (N_SYMBOLS - 1))) % N_SYMBOLS;
        const arr = [sym, sym, other];
        // shuffle
        for (let i = arr.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        targets = arr;
      } else {
        // random
        targets = [
          Math.floor(Math.random() * N_SYMBOLS),
          Math.floor(Math.random() * N_SYMBOLS),
          Math.floor(Math.random() * N_SYMBOLS),
        ];
      }

      const now = performance.now();
      game.reels.forEach((reel, i) => {
        reel.state = "spinning";
        reel.spinStartTime = now;
        reel.spinDuration = 1800 + i * 600 + Math.random() * 200;
        reel.startProgress = reel.progress;
        const minSpins = 6 + i * 2;
        // +0.5 cell-width so the cell *center* (not its edge) lands on the
        // win-line. Without it the reels stop on cell boundaries and the
        // bezel-slit shows two half-symbols.
        const targetOffset = (targets[i] + 0.5) / N_SYMBOLS;
        const baseRot = Math.floor(reel.progress);
        reel.endProgress = baseRot + minSpins + targetOffset;
        reel.targetSymbol = targets[i];
      });

      refreshDisplay();
      onPartActivateRef.current?.({ kind: "spin", bet: game.bet });
      // Slam the lever down and let the spring whip it back with overshoot.
      leverSpring.position = 1;
      leverSpring.velocity = -0.8;
    }

    function cycleBet() {
      if (game.status === "SPINNING") return;
      const idx = BET_OPTIONS.indexOf(game.bet);
      const next = BET_OPTIONS[(idx + 1) % BET_OPTIONS.length];
      game.bet = next;
      if (game.status === "INSERT") game.status = "IDLE";
      refreshDisplay();
      onPartActivateRef.current?.({ kind: "bet", value: next });
    }

    function addCredits() {
      game.credits += 100;
      if (game.status === "INSERT") game.status = "IDLE";
      refreshDisplay();
      onPartActivateRef.current?.({ kind: "credit", amount: 100 });
    }

    function maybeResolveSpin() {
      if (game.status !== "SPINNING") return;
      const allStopped = game.reels.every((r) => r.state === "stopped");
      if (!allStopped) return;
      const { outcome, winAmt } = evaluateOutcome();
      if (outcome === "JACKPOT") {
        game.credits += winAmt;
        game.lastWin = winAmt;
        game.status = "JACKPOT";
        game.winFlash = 1.4;
        spawnSparkles(70, true);
        onPartActivateRef.current?.({ kind: "jackpot", amount: winAmt });
      } else if (outcome === "WIN") {
        game.credits += winAmt;
        game.lastWin = winAmt;
        game.status = "WIN";
        game.winFlash = 1.0;
        spawnSparkles(35, false);
        onPartActivateRef.current?.({ kind: "win", amount: winAmt });
      } else {
        game.lastWin = 0;
        game.status = "LOSE";
        game.winFlash = 0;
        onPartActivateRef.current?.({ kind: "lose" });
      }
      game.revealedAt = performance.now();
      refreshDisplay();
    }

    function returnToIdle() {
      if (game.status === "WIN" || game.status === "LOSE" || game.status === "JACKPOT") {
        // After ~2.4s, flip back to IDLE
        if (performance.now() - game.revealedAt > 2400) {
          game.status = game.credits < game.bet ? "INSERT" : "IDLE";
          refreshDisplay();
        }
      }
    }

    // Lever spring — under-damped harmonic oscillator. Pulling the lever
    // sets position=1; the spring restores it to 0 with a brief overshoot,
    // giving the arm a real mechanical bounce instead of a linear decay.
    const leverSpring = { position: 0, velocity: 0 };
    const LEVER_K = 70;   // stiffness — higher = snappier return
    const LEVER_C = 6.5;  // damping  — lower than 2*sqrt(K) for overshoot

    // ============================================================
    // Cache motion seeds for explosion (after building everything)
    // ============================================================
    const parts = [];
    device.traverse((o) => {
      if (!o.isMesh) return;
      const baseDir = o.position.clone();
      if (baseDir.lengthSq() < 0.01) baseDir.set(0, 1, 0);
      baseDir.normalize();
      parts.push({
        mesh: o,
        basePos: o.position.clone(),
        baseRot: o.rotation.clone(),
        dir: baseDir,
        jitter: new THREE.Vector3(
          (Math.random() - 0.5) * 0.55,
          (Math.random() - 0.5) * 0.55,
          (Math.random() - 0.5) * 0.55,
        ),
        spin: new THREE.Vector3(
          (Math.random() - 0.5) * 1.4,
          (Math.random() - 0.5) * 1.4,
          (Math.random() - 0.5) * 1.4,
        ),
        phase: Math.random() * Math.PI * 2,
        baseEmissive: o.material?.emissiveIntensity ?? 0,
        isParticle: particles.some((p) => p.mesh === o),
      });
    });

    // ============================================================
    // Interaction
    // ============================================================
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const mouseTarget = new THREE.Vector2();
    const mouseLerp = new THREE.Vector2();

    let hoveredPart = null;
    const pulseUntil = new Map();

    let dragging = false;
    let dragStart = { x: 0, y: 0 };
    let manualYaw = 0;
    let manualPitch = 0;
    let manualYawTarget = 0;
    let manualPitchTarget = 0;

    const allHoverables = parts
      .filter((p) => !p.isParticle)
      .map((p) => p.mesh);

    const setCanvasInteractive = (yes) => {
      renderer.domElement.style.pointerEvents = yes ? "auto" : "none";
    };
    const setCursor = (style) => {
      document.body.style.cursor = style;
    };

    const updateNDC = (e) => {
      const rect = renderer.domElement.getBoundingClientRect();
      ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      mouseTarget.x = ndc.x;
      mouseTarget.y = ndc.y;
    };

    const raycastDevice = () => {
      raycaster.setFromCamera(ndc, camera);
      return raycaster.intersectObjects(allHoverables, false);
    };

    const onPointerMove = (e) => {
      updateNDC(e);

      if (dragging) {
        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;
        manualYawTarget += dx * 0.005;
        manualPitchTarget = THREE.MathUtils.clamp(manualPitchTarget + dy * 0.003, -0.5, 0.5);
        dragStart = { x: e.clientX, y: e.clientY };
        return;
      }

      const hits = raycastDevice();
      const hit = hits[0]?.object || null;
      hoveredPart = hit;

      if (hit) {
        setCanvasInteractive(true);
        setCursor(hit.userData?.interactive ? "pointer" : "grab");
      } else {
        setCanvasInteractive(false);
        setCursor("");
      }
    };

    const onPointerDown = (e) => {
      updateNDC(e);
      const hits = raycastDevice();
      const hit = hits[0]?.object;
      if (!hit) return;
      const ud = hit.userData || {};

      const triggerPress = (action) => {
        const btn = buttonByAction[action];
        if (btn?.userData.pressSpring) {
          btn.userData.pressSpring.position = 1;
          btn.userData.pressSpring.velocity = 0;
        }
      };
      if (ud.interactive && ud.action === "spin") {
        startSpin();
        pulseUntil.set(hit, performance.now() + 700);
        triggerPress("spin");
        e.preventDefault();
        return;
      }
      if (ud.interactive && ud.action === "bet") {
        cycleBet();
        pulseUntil.set(hit, performance.now() + 500);
        triggerPress("bet");
        e.preventDefault();
        return;
      }
      if (ud.interactive && ud.action === "credit") {
        addCredits();
        pulseUntil.set(hit, performance.now() + 500);
        triggerPress("credit");
        e.preventDefault();
        return;
      }
      if (ud.interactive && ud.action === "reel-click") {
        // Clicking any reel triggers a full spin (when not already spinning).
        startSpin();
        e.preventDefault();
        return;
      }

      // Otherwise: drag to rotate
      dragging = true;
      dragStart = { x: e.clientX, y: e.clientY };
      setCursor("grabbing");
      e.preventDefault();
    };

    const onPointerUp = () => {
      if (dragging) {
        dragging = false;
        setCursor("grab");
      }
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);

    // ============================================================
    // Resize
    // ============================================================
    const onResize = () => {
      const w = width();
      const h = height();
      camera.aspect = w / h;
      camera.fov = fovFor(w, h);
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      bgUniforms.uResolution.value.set(w, h);
      farStarsMat.uniforms.uPixelRatio.value = renderer.getPixelRatio();
    };
    window.addEventListener("resize", onResize);

    // ============================================================
    // Animation loop
    // ============================================================
    const clock = new THREE.Clock();
    let smoothed = 0;
    let rafId = 0;
    const tmpPos = new THREE.Vector3();
    const tmpJitter = new THREE.Vector3();

    const sectionIds = ["home", "about", "skills", "services", "contact"];

    const animate = () => {
      const dt = Math.min(clock.getDelta(), 0.06);
      const elapsed = clock.getElapsedTime();
      const target = progressRef?.current ?? 0;
      smoothed += (target - smoothed) * (1 - Math.exp(-dt * 6));

      // Chapter-driven camera shots
      const chapter = Math.max(
        0,
        Math.min(camPositions.length - 1, activeChapterRef.current | 0),
      );
      const camK = 1 - Math.exp(-dt * 2.4);
      camPosCurrent.lerp(camPositions[chapter], camK);
      camLookCurrent.lerp(camLooks[chapter], camK);

      // Mouse parallax
      mouseLerp.x += (mouseTarget.x - mouseLerp.x) * Math.min(1, dt * 6);
      mouseLerp.y += (mouseTarget.y - mouseLerp.y) * Math.min(1, dt * 6);
      camera.position.copy(camPosCurrent);
      camera.position.x += mouseLerp.x * 0.55;
      camera.position.y += mouseLerp.y * 0.35;
      camera.lookAt(camLookCurrent);

      // Drive the interactive shader background
      bgUniforms.uTime.value = elapsed;
      bgUniforms.uMouse.value.set(mouseLerp.x, mouseLerp.y);
      bgUniforms.uProgress.value = smoothed;
      bgUniforms.uPulse.value = Math.max(bgUniforms.uPulse.value * Math.exp(-dt * 1.4), game.winFlash);

      // Chapter palette flow — lerp shader uniforms toward current chapter's
      // palette so backgrounds smoothly transition as the user scrolls.
      const palLerpK = 1 - Math.exp(-dt * 1.6);
      const targetPal = CHAPTER_PALETTES[chapter];
      _palTargetBase.setHex(targetPal.base);
      _palTargetMid.setHex(targetPal.mid);
      _palTargetAcc.setHex(targetPal.acc);
      _palTargetWarm.setHex(targetPal.warm);
      bgUniforms.uPaletteBase.value.lerp(_palTargetBase, palLerpK);
      bgUniforms.uPaletteMid.value.lerp(_palTargetMid, palLerpK);
      bgUniforms.uPaletteAcc.value.lerp(_palTargetAcc, palLerpK);
      bgUniforms.uPaletteWarm.value.lerp(_palTargetWarm, palLerpK);
      bgUniforms.uChapter.value +=
        (chapter - bgUniforms.uChapter.value) * palLerpK;

      // Far parallax stars: subtle drift + mouse parallax (very small shift
      // because they're far — selling depth without making them obvious).
      farStarsMat.uniforms.uTime.value = elapsed;
      farStars.rotation.y = elapsed * 0.008;
      farStars.position.x = -mouseLerp.x * 0.6;
      farStars.position.y = -mouseLerp.y * 0.4;

      // Mid-ground gas clouds: tint toward chapter accent + slow bob/drift.
      // They're close enough that mouse parallax is felt distinctly from stars.
      for (let gi = 0; gi < gasSprites.length; gi++) {
        const s = gasSprites[gi];
        s.material.color.lerp(_palTargetAcc, palLerpK);
        const ud = s.userData;
        s.position.y =
          ud.baseY + Math.sin(elapsed * ud.bobSpd + ud.phase) * ud.bobAmp;
        s.position.x =
          ud.baseX + Math.cos(elapsed * ud.bobSpd * 0.7 + ud.phase) * ud.bobAmp * 0.4;
      }

      // Cursor ray in world space — origin at the (parallax-adjusted) camera,
      // direction through the smoothed cursor NDC. Used below to give each
      // chapter floater a proximity-driven pull + pop toward the cursor.
      _cursorRayOrigin.copy(camera.position);
      _cursorRayDir
        .set(mouseLerp.x, mouseLerp.y, 0.5)
        .unproject(camera)
        .sub(_cursorRayOrigin)
        .normalize();
      // Guard against the rare horizontal-camera case where dir.z ≈ 0
      const cursorDirZ =
        Math.abs(_cursorRayDir.z) < 0.001
          ? (_cursorRayDir.z < 0 ? -0.001 : 0.001)
          : _cursorRayDir.z;

      // Per-chapter floating objects: cross-fade visibility, animate each
      // active set with drift + bob + spin + per-layer parallax + cursor pull.
      const visLerpK = 1 - Math.exp(-dt * 2.0);
      // Cursor attraction tuning — quadratic falloff inside ATTRACT_RADIUS,
      // capped pull keeps motion subtle so it reads as "alive", not jittery.
      const ATTRACT_RADIUS = 3.0;
      const ATTRACT_RADIUS_SQ = ATTRACT_RADIUS * ATTRACT_RADIUS;
      const ATTRACT_PULL = 0.42;
      const ATTRACT_SCALE = 0.22;
      for (let c = 0; c < 5; c++) {
        const im = chapterMeshes[c];
        const target = c === chapter ? 1.0 : 0.0;
        im.material.opacity = lerp(im.material.opacity, target, visLerpK);
        // Skip matrix update when essentially invisible (perf win)
        if (im.material.opacity < 0.005) continue;

        const states = chapterStates[c];
        for (let i = 0; i < states.length; i++) {
          const s = states[i];
          // Drift upward, wrap every 12 units so the field stays continuous
          const yDrift = ((s.driftPhase + elapsed * s.driftSpeed) % 12) - 6;
          let x = s.basePos.x + Math.cos(elapsed * s.bobSpd + s.bobPhase) * s.bobAmp;
          let y = s.basePos.y + yDrift;
          const z = s.basePos.z;
          // Layer-graded parallax: far barely shifts, near shifts strongly
          const parX = s.layer === 0 ? 0.18 : s.layer === 1 ? 0.55 : 1.15;
          const parY = s.layer === 0 ? 0.12 : s.layer === 1 ? 0.35 : 0.7;
          x += mouseLerp.x * parX;
          y += mouseLerp.y * parY;

          // Project cursor ray onto this instance's z-plane, then measure 2D
          // distance — closer instances pull harder and scale up subtly.
          // Near layer (layer 2) gets the strongest response; far layer is
          // muted so depth still reads as depth.
          const tRay = (z - _cursorRayOrigin.z) / cursorDirZ;
          const cx = _cursorRayOrigin.x + _cursorRayDir.x * tRay;
          const cy = _cursorRayOrigin.y + _cursorRayDir.y * tRay;
          const dx = cx - x;
          const dy = cy - y;
          const distSq = dx * dx + dy * dy;
          let scaleBoost = 1;
          if (distSq < ATTRACT_RADIUS_SQ) {
            const proximity = 1 - distSq / ATTRACT_RADIUS_SQ;   // 0..1
            const eased = proximity * proximity;                 // ease-in
            const layerGain = s.layer === 0 ? 0.35 : s.layer === 1 ? 0.7 : 1.0;
            const pull = eased * ATTRACT_PULL * layerGain;
            x += dx * pull;
            y += dy * pull;
            scaleBoost = 1 + eased * ATTRACT_SCALE * layerGain;
          }

          _objDummy.position.set(x, y, z);
          _objQuat.setFromAxisAngle(s.rotAxis, elapsed * s.rotSpeed + s.rotPhase);
          _objDummy.quaternion.copy(_objQuat);
          _objDummy.scale.setScalar(s.scale * scaleBoost);
          _objDummy.updateMatrix();
          im.setMatrixAt(i, _objDummy.matrix);
        }
        im.instanceMatrix.needsUpdate = true;
      }

      const activeSectionId = sectionIds[chapter];

      // Manual rotation (drag) — always eases back toward 0 so the device
      // returns to facing the camera. Faster decay rate so the user doesn't
      // get "stuck" looking at the back of the machine.
      manualYaw += (manualYawTarget - manualYaw) * (1 - Math.exp(-dt * 7));
      manualPitch += (manualPitchTarget - manualPitch) * (1 - Math.exp(-dt * 7));
      if (!dragging) {
        // Decay target back to 0 (forward-facing) over ~1.5s
        manualYawTarget *= 1 - Math.min(1, dt * 1.6);
        manualPitchTarget *= 1 - Math.min(1, dt * 1.6);
        // Snap tiny residuals to 0 to fully settle
        if (Math.abs(manualYawTarget) < 0.002) manualYawTarget = 0;
        if (Math.abs(manualPitchTarget) < 0.002) manualPitchTarget = 0;
      }

      // Idle "breathing" animation — gentle wobble + slow bob, very subtle
      // so the device feels alive but always reads as facing front.
      const idleYaw = Math.sin(elapsed * 0.55) * 0.045;
      const idlePitch = Math.sin(elapsed * 0.42) * 0.035 - 0.02;

      device.rotation.y = idleYaw + manualYaw;
      device.rotation.x = idlePitch + manualPitch;

      // Cabinet jolt — high-frequency dual-sine "rattle" on win states,
      // amplitude tracking winFlash so it eases out as the celebration
      // fades. Tiny on regular wins, more pronounced on jackpots.
      const shakePeak =
        game.status === "JACKPOT" ? 0.022 :
        game.status === "WIN" ? 0.008 : 0;
      const shakeAmp = shakePeak * Math.min(1, game.winFlash);
      if (shakeAmp > 0) {
        device.position.x =
          Math.sin(elapsed * 47.3) * Math.cos(elapsed * 31.7) * shakeAmp;
        device.position.y =
          Math.sin(elapsed * 53.1 + 1.7) * shakeAmp * 0.7;
      } else if (device.position.x !== 0 || device.position.y !== 0) {
        device.position.x = 0;
        device.position.y = 0;
      }

      // Explosion
      const explode = smoothstep(0.36, 0.5, smoothed) - smoothstep(0.62, 0.78, smoothed);
      const distance = 1.7 * explode;

      const now = performance.now();

      // Update reel spin progress
      for (const reel of game.reels) {
        if (reel.state === "spinning") {
          const t = Math.min(1, (now - reel.spinStartTime) / reel.spinDuration);
          if (t >= 1) {
            reel.progress = reel.endProgress;
            reel.state = "stopped";
            reel.currentSymbol = reel.targetSymbol;
          } else {
            const eased = easeOutQuint(t);
            reel.progress = lerp(reel.startProgress, reel.endProgress, eased);
          }
        }
        // Always update the texture offsets from progress (modulo 1)
        const off = reel.progress - Math.floor(reel.progress);
        reel.material.map.offset.x = off;
        reel.material.emissiveMap.offset.x = off;
      }

      maybeResolveSpin();
      returnToIdle();

      // Win-flash decay
      if (game.winFlash > 0) {
        game.winFlash = Math.max(0, game.winFlash - dt * 0.7);
      }

      // Drive winLight intensity from flash
      const flashPulse =
        game.winFlash * (1 + 0.3 * Math.sin(elapsed * 22));
      winLight.intensity = flashPulse * 4;
      winLight.color.setHex(game.status === "JACKPOT" ? 0xffd454 : 0x9ff5c8);

      // Beacon: spins when winning, idle slow rotation otherwise
      const beaconActive = game.status === "JACKPOT" || game.status === "WIN" ? 1 : 0;
      beaconLight.intensity = lerp(
        beaconLight.intensity,
        beaconActive ? 1.6 : 0.05,
        1 - Math.exp(-dt * 4),
      );
      beaconBulb.material.emissiveIntensity = lerp(
        beaconBulb.material.emissiveIntensity,
        beaconActive ? 4 + 1.5 * Math.sin(elapsed * 16) : 1.4,
        1 - Math.exp(-dt * 6),
      );

      // Lever spring step (semi-implicit Euler). Target rest = 0; the
      // restoring force pulls position back, damping kills it slowly enough
      // to leave a single visible bounce. Sub-step so high stiffness stays
      // stable across long frames.
      {
        const subSteps = 3;
        const sdt = dt / subSteps;
        for (let s = 0; s < subSteps; s++) {
          const force = -LEVER_K * leverSpring.position;
          const damp = -LEVER_C * leverSpring.velocity;
          leverSpring.velocity += (force + damp) * sdt;
          leverSpring.position += leverSpring.velocity * sdt;
        }
      }
      leverPivot.rotation.x = leverSpring.position;

      // Update particles
      updateParticles(dt);

      // Update parts (motion + emissive)
      for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        if (p.isParticle) continue;
        const float = explode * 0.07 * Math.sin(elapsed * 1.1 + p.phase);
        tmpPos.copy(p.basePos)
          .addScaledVector(p.dir, distance)
          .add(tmpJitter.copy(p.jitter).multiplyScalar(distance * 0.5));
        tmpPos.y += float;
        p.mesh.position.copy(tmpPos);
        p.mesh.rotation.set(
          p.baseRot.x + p.spin.x * explode,
          p.baseRot.y + p.spin.y * explode,
          p.baseRot.z + p.spin.z * explode,
        );

        if (
          p.mesh.material?.emissiveIntensity !== undefined &&
          !p.mesh.userData?.customAnim
        ) {
          const pulseEnd = pulseUntil.get(p.mesh) || 0;
          const pulseAmt = pulseEnd > now ? (pulseEnd - now) / 700 : 0;
          const isHovered = hoveredPart === p.mesh && p.mesh.userData?.interactive;
          // Idle blink for marquee LEDs (indicators are driven separately).
          let idleBoost = 0;
          const partName = p.mesh.userData?.partName || "";
          if (partName.startsWith("marquee-led-")) {
            const ph = p.mesh.userData.blinkPhase || 0;
            const fast = game.status === "JACKPOT" || game.status === "WIN" ? 6.5 : 1.6;
            idleBoost = 0.5 + 0.5 * Math.sin(elapsed * fast + ph);
          }
          // Win-flash boost on the bezel/win line/reels
          let flashBoost = 0;
          if (game.winFlash > 0 && p.mesh.material === winLineMat) {
            flashBoost = 4 * game.winFlash;
          }
          let spinBoost = 0;
          for (let ri = 0; ri < reelMats.length; ri++) {
            if (p.mesh.material !== reelMats[ri]) continue;
            if (game.winFlash > 0) flashBoost = 1.4 * game.winFlash;
            const reel = game.reels[ri];
            if (reel.state === "spinning") {
              // Match the easeOutQuint velocity profile: bright at launch,
              // fading as the reel decelerates onto its symbol.
              const t = Math.min(
                1,
                (now - reel.spinStartTime) / reel.spinDuration,
              );
              const oneMinusT = 1 - t;
              spinBoost = 1.1 * oneMinusT * oneMinusT * oneMinusT * oneMinusT;
            }
            break;
          }
          // Spin button pulses while idle and ready
          let readyPulse = 0;
          if (
            partName === "button-SPIN" &&
            game.status === "IDLE" &&
            game.credits >= game.bet
          ) {
            readyPulse = 0.45 + 0.4 * Math.sin(elapsed * 3.2);
          }
          // Lever ball pulses subtly when idle ready
          if (
            partName === "lever-ball" &&
            game.status === "IDLE" &&
            game.credits >= game.bet
          ) {
            readyPulse = 0.35 + 0.3 * Math.sin(elapsed * 2.6);
          }
          const targetIntensity =
            p.baseEmissive +
            idleBoost +
            flashBoost +
            spinBoost +
            readyPulse +
            (isHovered ? 0.7 : 0) +
            pulseAmt * 1.4;
          p.mesh.material.emissiveIntensity = lerp(
            p.mesh.material.emissiveIntensity,
            targetIntensity,
            1 - Math.exp(-dt * 12),
          );
        }
      }

      // ----------------------------------------------------------
      // Button press depression — critically-damped spring per button.
      // Position 1 = fully sunk; restores to 0 with no overshoot so the
      // button reads as "snappy" rather than rubbery.
      // ----------------------------------------------------------
      {
        const BTN_K = 320;
        const BTN_C = 36;        // ~2*sqrt(K) → critical damping
        const BTN_DEPTH = 0.045; // world units sunk at full press
        const subSteps = 3;
        const sdt = dt / subSteps;
        for (const action in buttonByAction) {
          const btn = buttonByAction[action];
          const sp = btn.userData.pressSpring;
          if (!sp) continue;
          for (let s = 0; s < subSteps; s++) {
            const force = -BTN_K * sp.position;
            const damp = -BTN_C * sp.velocity;
            sp.velocity += (force + damp) * sdt;
            sp.position += sp.velocity * sdt;
          }
          if (sp.position > 0.0005 || Math.abs(sp.velocity) > 0.0005) {
            const depth = sp.position * BTN_DEPTH;
            btn.position.x += buttonPressAxis.x * depth;
            btn.position.y += buttonPressAxis.y * depth;
            btn.position.z += buttonPressAxis.z * depth;
          }
        }
      }

      // ----------------------------------------------------------
      // Chase indicator lights — traveling pulse with state response
      // ----------------------------------------------------------
      // Per-state tuning. The pulse "travels" left→right at chaseSpeed
      // cycles per second; pulseW is the width of the bright band.
      let chaseSpeed = 0.55;
      let pulseW = 0.42;
      let baseLevel = 0.55;
      let peakBoost = 1.8;
      let haloPeak = 0.55;
      let rainbow = false;
      if (game.status === "SPINNING") {
        chaseSpeed = 1.55; pulseW = 0.34; baseLevel = 0.85;
        peakBoost = 2.4; haloPeak = 0.85;
      } else if (game.status === "WIN") {
        chaseSpeed = 2.4; pulseW = 0.5; baseLevel = 1.4;
        peakBoost = 3.0; haloPeak = 1.1;
      } else if (game.status === "JACKPOT") {
        chaseSpeed = 4.2; pulseW = 0.55; baseLevel = 1.9;
        peakBoost = 3.6; haloPeak = 1.35;
        rainbow = true;
      }
      const cyclePhase = (elapsed * chaseSpeed) % 1;
      const colorLerpK = 1 - Math.exp(-dt * 5);
      const intensityLerpK = 1 - Math.exp(-dt * 14);
      for (let i = 0; i < indicators.length; i++) {
        const ind = indicators[i];
        // Wrap-aware distance from current cycle position to this indicator.
        let d = Math.abs(((cyclePhase - ind.basePhase) % 1 + 1.5) % 1 - 0.5);
        const t = Math.max(0, 1 - d / pulseW);
        const sharp = t * t * (3 - 2 * t);
        const targetIntensity = baseLevel + sharp * peakBoost;
        const targetHalo = 0.18 + sharp * haloPeak;

        ind.material.emissiveIntensity = lerp(
          ind.material.emissiveIntensity, targetIntensity, intensityLerpK,
        );
        ind.haloMat.opacity = lerp(
          ind.haloMat.opacity, targetHalo, intensityLerpK,
        );

        if (rainbow) {
          const hue = (elapsed * 0.7 + i * 0.18) % 1;
          ind.material.emissive.setHSL(hue, 1.0, 0.55);
          ind.haloMat.color.setHSL(hue, 1.0, 0.65);
        } else {
          ind.material.emissive.lerp(ind.baseColor, colorLerpK);
          ind.haloMat.color.lerp(ind.baseColor, colorLerpK);
        }
      }

      // Suppress unused-var warning while keeping the section ID logic in
      // place for future per-chapter device states.
      void activeSectionId;

      renderer.render(scene, camera);
      rafId = requestAnimationFrame(animate);
    };
    rafId = requestAnimationFrame(animate);

    // ============================================================
    // Cleanup
    // ============================================================
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      document.body.style.cursor = "";
      pmrem.dispose();
      envTexture.dispose();
      noiseNormal.dispose();
      brandTex.dispose();
      marqueeTex.dispose();
      payoutChartTex.dispose();
      displayTex.dispose();
      glowTex.dispose();
      floorPoolTex.dispose();
      reelTextures.forEach((t) => t.dispose());
      Object.values(buttonLabels).forEach((t) => t.dispose());
      geos.forEach((g) => g.dispose());
      allMaterials.forEach((m) => m.dispose());
      bgGeometry.dispose();
      bgMaterial.dispose();
      farStarsGeo.dispose();
      farStarsMat.dispose();
      chapterGeoms.forEach((g) => g.dispose());
      chapterMats.forEach((m) => m.dispose());
      chapterMeshes.forEach((im) => scene.remove(im));
      gasSprites.forEach((s) => s.material.dispose());
      gasTex.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [progressRef, activeChapterRef]);

  return <div ref={containerRef} className="hero3d-canvas" />;
};

export default Hero3D;
